/* eslint-disable @typescript-eslint/no-empty-object-type */
/**
 * Generated by @openapi-codegen
 *
 * @version 1.0.0
 */
import { REQUEST_SESSION_ID_HEADER } from '@aloft/toolkit-shared';
import * as reactQuery from '@tanstack/react-query';

import { ToolkitContext, queryKeyFn, useToolkitContext } from './toolkitContext.ts';
import type * as Fetcher from './toolkitFetcher.ts';
import { toolkitFetch } from './toolkitFetcher.ts';
import type * as Responses from './toolkitResponses.ts';
import type * as Schemas from './toolkitSchemas.ts';
import { deepMerge } from './toolkitUtils.ts';

type QueryFnOptions = {
  signal?: AbortController['signal'];
};

export type CreateBlobError = Fetcher.ErrorWrapper<undefined>;

export type CreateBlobVariables = {
  body?: Schemas.BlobRequest;
} & ToolkitContext['fetcherOptions'];

export const fetchCreateBlob = (variables: CreateBlobVariables, signal?: AbortSignal) =>
  toolkitFetch<undefined, CreateBlobError, Schemas.BlobRequest, {}, {}, {}>({
    url: '/api/v1/blobs',
    method: 'put',
    ...variables,
    signal,
  });

export const useCreateBlob = (
  options?: Omit<
    reactQuery.UseMutationOptions<undefined, CreateBlobError, CreateBlobVariables>,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<undefined, CreateBlobError, CreateBlobVariables>({
    mutationFn: (variables: CreateBlobVariables) =>
      fetchCreateBlob(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetBlobPathParams = {
  /**
   * Unique identifier for a `Blob`
   */
  blobID: string;
};

export type GetBlobError = Fetcher.ErrorWrapper<undefined>;

export type GetBlobVariables = {
  pathParams: GetBlobPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchGetBlob = (variables: GetBlobVariables, signal?: AbortSignal) =>
  toolkitFetch<Schemas.Blob, GetBlobError, undefined, {}, {}, GetBlobPathParams>({
    url: '/api/v1/blobs/{blobID}',
    method: 'get',
    ...variables,
    signal,
  });

export function getBlobQuery(variables: GetBlobVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Blob>;
};

export function getBlobQuery(variables: GetBlobVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Blob>;
};

export function getBlobQuery(variables: GetBlobVariables) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v1/blobs/{blobID}',
      operationId: 'getBlob',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) => fetchGetBlob(variables, signal),
  };
}

export const useSuspenseGetBlob = <TData = Schemas.Blob>(
  variables: GetBlobVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Blob, GetBlobError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<Schemas.Blob, GetBlobError, TData>({
    ...getBlobQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetBlob = <TData = Schemas.Blob>(
  variables: GetBlobVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Blob, GetBlobError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Blob, GetBlobError, TData>({
    ...getBlobQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type GetBlobMetadataPathParams = {
  /**
   * Unique identifier for a `Blob`
   */
  blobID: string;
};

export type GetBlobMetadataError = Fetcher.ErrorWrapper<undefined>;

export type GetBlobMetadataVariables = {
  pathParams: GetBlobMetadataPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchGetBlobMetadata = (variables: GetBlobMetadataVariables, signal?: AbortSignal) =>
  toolkitFetch<
    Schemas.Response,
    GetBlobMetadataError,
    undefined,
    {},
    {},
    GetBlobMetadataPathParams
  >({
    url: '/api/v1/blobs/{blobID}/metadata',
    method: 'get',
    ...variables,
    signal,
  });

export function getBlobMetadataQuery(variables: GetBlobMetadataVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getBlobMetadataQuery(variables: GetBlobMetadataVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getBlobMetadataQuery(variables: GetBlobMetadataVariables) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v1/blobs/{blobID}/metadata',
      operationId: 'getBlobMetadata',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) => fetchGetBlobMetadata(variables, signal),
  };
}

export const useSuspenseGetBlobMetadata = <TData = Schemas.Response>(
  variables: GetBlobMetadataVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetBlobMetadataError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<Schemas.Response, GetBlobMetadataError, TData>({
    ...getBlobMetadataQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetBlobMetadata = <TData = Schemas.Response>(
  variables: GetBlobMetadataVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetBlobMetadataError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, GetBlobMetadataError, TData>({
    ...getBlobMetadataQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type GetAccountError = Fetcher.ErrorWrapper<undefined>;

export type GetAccountVariables = ToolkitContext['fetcherOptions'];

/**
 * Gets a `Account` entity for the authenticated email.
 */
export const fetchGetAccount = (variables: GetAccountVariables, signal?: AbortSignal) =>
  toolkitFetch<Schemas.Response, GetAccountError, undefined, {}, {}, {}>({
    url: '/api/v1/account',
    method: 'get',
    ...variables,
    signal,
  });

/**
 * Gets a `Account` entity for the authenticated email.
 */
export function getAccountQuery(variables: GetAccountVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getAccountQuery(variables: GetAccountVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getAccountQuery(variables: GetAccountVariables) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v1/account',
      operationId: 'getAccount',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) => fetchGetAccount(variables, signal),
  };
}

/**
 * Gets a `Account` entity for the authenticated email.
 */
export const useSuspenseGetAccount = <TData = Schemas.Response>(
  variables: GetAccountVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetAccountError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<Schemas.Response, GetAccountError, TData>({
    ...getAccountQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

/**
 * Gets a `Account` entity for the authenticated email.
 */
export const useGetAccount = <TData = Schemas.Response>(
  variables: GetAccountVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetAccountError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, GetAccountError, TData>({
    ...getAccountQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type UpdateAccountError = Fetcher.ErrorWrapper<undefined>;

export type UpdateAccountVariables = {
  body?: Schemas.Request;
} & ToolkitContext['fetcherOptions'];

/**
 * Updates an instance of `Account`.
 */
export const fetchUpdateAccount = (variables: UpdateAccountVariables, signal?: AbortSignal) =>
  toolkitFetch<Schemas.Response, UpdateAccountError, Schemas.Request, {}, {}, {}>({
    url: '/api/v1/account',
    method: 'put',
    ...variables,
    signal,
  });

/**
 * Updates an instance of `Account`.
 */
export const useUpdateAccount = (
  options?: Omit<
    reactQuery.UseMutationOptions<Schemas.Response, UpdateAccountError, UpdateAccountVariables>,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<Schemas.Response, UpdateAccountError, UpdateAccountVariables>({
    mutationFn: (variables: UpdateAccountVariables) =>
      fetchUpdateAccount(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetListingsError = Fetcher.ErrorWrapper<undefined>;

export type GetListingsVariables = {
  body?: Schemas.ListingsRequest;
} & ToolkitContext['fetcherOptions'];

export const fetchGetListings = (variables: GetListingsVariables, signal?: AbortSignal) =>
  toolkitFetch<Responses.ListingsResponse, GetListingsError, Schemas.ListingsRequest, {}, {}, {}>({
    url: '/api/v1/listings',
    method: 'post',
    ...variables,
    signal,
  });

export const useGetListings = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Responses.ListingsResponse,
      GetListingsError,
      GetListingsVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<Responses.ListingsResponse, GetListingsError, GetListingsVariables>(
    {
      mutationFn: (variables: GetListingsVariables) =>
        fetchGetListings(deepMerge(fetcherOptions, variables)),
      ...options,
    },
  );
};

export type GetListingsByRadiusError = Fetcher.ErrorWrapper<undefined>;

export type GetListingsByRadiusVariables = {
  body?: Schemas.Request;
} & ToolkitContext['fetcherOptions'];

export const fetchGetListingsByRadius = (
  variables: GetListingsByRadiusVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<Schemas.Response, GetListingsByRadiusError, Schemas.Request, {}, {}, {}>({
    url: '/api/v1/listings/radius',
    method: 'post',
    ...variables,
    signal,
  });

export const useGetListingsByRadius = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      GetListingsByRadiusError,
      GetListingsByRadiusVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    Schemas.Response,
    GetListingsByRadiusError,
    GetListingsByRadiusVariables
  >({
    mutationFn: (variables: GetListingsByRadiusVariables) =>
      fetchGetListingsByRadius(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetListingsByPolygonError = Fetcher.ErrorWrapper<undefined>;

export type GetListingsByPolygonVariables = {
  body?: Schemas.Request;
} & ToolkitContext['fetcherOptions'];

export const fetchGetListingsByPolygon = (
  variables: GetListingsByPolygonVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<Schemas.Response, GetListingsByPolygonError, Schemas.Request, {}, {}, {}>({
    url: '/api/v1/listings/poly',
    method: 'post',
    ...variables,
    signal,
  });

export const useGetListingsByPolygon = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      GetListingsByPolygonError,
      GetListingsByPolygonVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    Schemas.Response,
    GetListingsByPolygonError,
    GetListingsByPolygonVariables
  >({
    mutationFn: (variables: GetListingsByPolygonVariables) =>
      fetchGetListingsByPolygon(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetTkv2OrdersAPIQueryParams = {
  limit?: number;
  next?: string;
};

export type GetTkv2OrdersAPIError = Fetcher.ErrorWrapper<undefined>;

export type GetTkv2OrdersAPIVariables = {
  queryParams?: GetTkv2OrdersAPIQueryParams;
} & ToolkitContext['fetcherOptions'];

/**
 * Gets a list of all `Order` entities.
 */
export const fetchGetTkv2OrdersAPI = (variables: GetTkv2OrdersAPIVariables, signal?: AbortSignal) =>
  toolkitFetch<
    Schemas.Response,
    GetTkv2OrdersAPIError,
    undefined,
    {},
    GetTkv2OrdersAPIQueryParams,
    {}
  >({ url: '/api/v2/orders', method: 'get', ...variables, signal });

/**
 * Gets a list of all `Order` entities.
 */
export function getTkv2OrdersApiQuery(variables: GetTkv2OrdersAPIVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getTkv2OrdersApiQuery(variables: GetTkv2OrdersAPIVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getTkv2OrdersApiQuery(variables: GetTkv2OrdersAPIVariables) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v2/orders',
      operationId: 'getTkv2OrdersAPI',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) => fetchGetTkv2OrdersAPI(variables, signal),
  };
}

/**
 * Gets a list of all `Order` entities.
 */
export const useSuspenseGetTkv2OrdersAPI = <TData = Schemas.Response>(
  variables: GetTkv2OrdersAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetTkv2OrdersAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<Schemas.Response, GetTkv2OrdersAPIError, TData>({
    ...getTkv2OrdersApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

/**
 * Gets a list of all `Order` entities.
 */
export const useGetTkv2OrdersAPI = <TData = Schemas.Response>(
  variables: GetTkv2OrdersAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetTkv2OrdersAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, GetTkv2OrdersAPIError, TData>({
    ...getTkv2OrdersApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type CreateTkv2OrderAPIError = Fetcher.ErrorWrapper<undefined>;

export type CreateTkv2OrderAPIVariables = {
  body?: Schemas.GeneralRequestType;
} & ToolkitContext['fetcherOptions'];

/**
 * Creates a new instance of a `Order`.
 */
export const fetchCreateTkv2OrderAPI = (
  variables: CreateTkv2OrderAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<Schemas.Response, CreateTkv2OrderAPIError, Schemas.GeneralRequestType, {}, {}, {}>({
    url: '/api/v2/orders',
    method: 'post',
    ...variables,
    signal,
  });

/**
 * Creates a new instance of a `Order`.
 */
export const useCreateTkv2OrderAPI = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      CreateTkv2OrderAPIError,
      CreateTkv2OrderAPIVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    Schemas.Response,
    CreateTkv2OrderAPIError,
    CreateTkv2OrderAPIVariables
  >({
    mutationFn: (variables: CreateTkv2OrderAPIVariables) =>
      fetchCreateTkv2OrderAPI(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type CreateAllTrainingOrdersAPIError = Fetcher.ErrorWrapper<undefined>;

export type CreateAllTrainingOrdersAPIVariables = {
  body?: Schemas.GeneralRequestType;
} & ToolkitContext['fetcherOptions'];

/**
 * Creates all three training orders
 */
export const fetchCreateAllTrainingOrdersAPI = (
  variables: CreateAllTrainingOrdersAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    CreateAllTrainingOrdersAPIError,
    Schemas.GeneralRequestType,
    {},
    {},
    {}
  >({ url: '/api/v2/allTrainingOrders', method: 'put', ...variables, signal });

/**
 * Creates all three training orders
 */
export const useCreateAllTrainingOrdersAPI = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      CreateAllTrainingOrdersAPIError,
      CreateAllTrainingOrdersAPIVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    Schemas.Response,
    CreateAllTrainingOrdersAPIError,
    CreateAllTrainingOrdersAPIVariables
  >({
    mutationFn: (variables: CreateAllTrainingOrdersAPIVariables) =>
      fetchCreateAllTrainingOrdersAPI(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type CreateInspectifyOrderAPIError = Fetcher.ErrorWrapper<undefined>;

export type CreateInspectifyOrderAPIVariables = {
  body?: Schemas.Request;
} & ToolkitContext['fetcherOptions'];

/**
 * Creates a new instance of an Inspectify Order.
 */
export const fetchCreateInspectifyOrderAPI = (
  variables: CreateInspectifyOrderAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<Schemas.Response, CreateInspectifyOrderAPIError, Schemas.Request, {}, {}, {}>({
    url: '/api/v2/inspectifyOrders',
    method: 'post',
    ...variables,
    signal,
  });

/**
 * Creates a new instance of an Inspectify Order.
 */
export const useCreateInspectifyOrderAPI = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      CreateInspectifyOrderAPIError,
      CreateInspectifyOrderAPIVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    Schemas.Response,
    CreateInspectifyOrderAPIError,
    CreateInspectifyOrderAPIVariables
  >({
    mutationFn: (variables: CreateInspectifyOrderAPIVariables) =>
      fetchCreateInspectifyOrderAPI(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type CreateRevisionOrderAPIError = Fetcher.ErrorWrapper<undefined>;

export type CreateRevisionOrderAPIVariables = {
  body?: Schemas.Request;
} & ToolkitContext['fetcherOptions'];

/**
 * Creates a new instance of a Revision Order.
 */
export const fetchCreateRevisionOrderAPI = (
  variables: CreateRevisionOrderAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<Schemas.Response, CreateRevisionOrderAPIError, Schemas.Request, {}, {}, {}>({
    url: '/api/v2/revisions',
    method: 'post',
    ...variables,
    signal,
  });

/**
 * Creates a new instance of a Revision Order.
 */
export const useCreateRevisionOrderAPI = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      CreateRevisionOrderAPIError,
      CreateRevisionOrderAPIVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    Schemas.Response,
    CreateRevisionOrderAPIError,
    CreateRevisionOrderAPIVariables
  >({
    mutationFn: (variables: CreateRevisionOrderAPIVariables) =>
      fetchCreateRevisionOrderAPI(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type PrefillOrderAPIError = Fetcher.ErrorWrapper<undefined>;

export type PrefillOrderAPIVariables = {
  body?: Schemas.Request;
} & ToolkitContext['fetcherOptions'];

/**
 * Fetch all external data and prefill the order.
 */
export const fetchPrefillOrderAPI = (variables: PrefillOrderAPIVariables, signal?: AbortSignal) =>
  toolkitFetch<Schemas.Response, PrefillOrderAPIError, Schemas.Request, {}, {}, {}>({
    url: '/api/v2/prefillOrders',
    method: 'post',
    ...variables,
    signal,
  });

/**
 * Fetch all external data and prefill the order.
 */
export const usePrefillOrderAPI = (
  options?: Omit<
    reactQuery.UseMutationOptions<Schemas.Response, PrefillOrderAPIError, PrefillOrderAPIVariables>,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<Schemas.Response, PrefillOrderAPIError, PrefillOrderAPIVariables>({
    mutationFn: (variables: PrefillOrderAPIVariables) =>
      fetchPrefillOrderAPI(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetTkv2OrderAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type GetTkv2OrderAPIError = Fetcher.ErrorWrapper<undefined>;

export type GetTkv2OrderAPIVariables = {
  pathParams: GetTkv2OrderAPIPathParams;
} & ToolkitContext['fetcherOptions'];

/**
 * Gets the details of a single instance of a `Order`.
 */
export const fetchGetTkv2OrderAPI = (variables: GetTkv2OrderAPIVariables, signal?: AbortSignal) =>
  toolkitFetch<
    Schemas.Response,
    GetTkv2OrderAPIError,
    undefined,
    {},
    {},
    GetTkv2OrderAPIPathParams
  >({ url: '/api/v2/orders/{orderID}', method: 'get', ...variables, signal });

/**
 * Gets the details of a single instance of a `Order`.
 */
export function getTkv2OrderApiQuery(variables: GetTkv2OrderAPIVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getTkv2OrderApiQuery(variables: GetTkv2OrderAPIVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getTkv2OrderApiQuery(variables: GetTkv2OrderAPIVariables) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v2/orders/{orderID}',
      operationId: 'getTkv2OrderAPI',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) => fetchGetTkv2OrderAPI(variables, signal),
  };
}

/**
 * Gets the details of a single instance of a `Order`.
 */
export const useSuspenseGetTkv2OrderAPI = <TData = Schemas.Response>(
  variables: GetTkv2OrderAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetTkv2OrderAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<Schemas.Response, GetTkv2OrderAPIError, TData>({
    ...getTkv2OrderApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

/**
 * Gets the details of a single instance of a `Order`.
 */
export const useGetTkv2OrderAPI = <TData = Schemas.Response>(
  variables: GetTkv2OrderAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetTkv2OrderAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, GetTkv2OrderAPIError, TData>({
    ...getTkv2OrderApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type UpdateTkv2OrderAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type UpdateTkv2OrderAPIError = Fetcher.ErrorWrapper<undefined>;

export type UpdateTkv2OrderAPIVariables = {
  body?: Schemas.GeneralRequestType;
  pathParams: UpdateTkv2OrderAPIPathParams;
} & ToolkitContext['fetcherOptions'];

/**
 * Updates the details of a single order.
 */
export const fetchUpdateTkv2OrderAPI = (
  variables: UpdateTkv2OrderAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    undefined,
    UpdateTkv2OrderAPIError,
    Schemas.GeneralRequestType,
    {},
    {},
    UpdateTkv2OrderAPIPathParams
  >({ url: '/api/v2/orders/{orderID}', method: 'put', ...variables, signal });

/**
 * Updates the details of a single order.
 */
export const useUpdateTkv2OrderAPI = (
  options?: Omit<
    reactQuery.UseMutationOptions<undefined, UpdateTkv2OrderAPIError, UpdateTkv2OrderAPIVariables>,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<undefined, UpdateTkv2OrderAPIError, UpdateTkv2OrderAPIVariables>({
    mutationFn: (variables: UpdateTkv2OrderAPIVariables) =>
      fetchUpdateTkv2OrderAPI(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type CompleteTkv2OrderAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type CompleteTkv2OrderAPIError = Fetcher.ErrorWrapper<undefined>;

export type CompleteTkv2OrderAPIVariables = {
  body?: Schemas.GeneralRequestType;
  pathParams: CompleteTkv2OrderAPIPathParams;
} & ToolkitContext['fetcherOptions'];

/**
 * Marks an order as complete.
 */
export const fetchCompleteTkv2OrderAPI = (
  variables: CompleteTkv2OrderAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    undefined,
    CompleteTkv2OrderAPIError,
    Schemas.GeneralRequestType,
    {},
    {},
    CompleteTkv2OrderAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/complete',
    method: 'put',
    ...variables,
    signal,
  });

/**
 * Marks an order as complete.
 */
export const useCompleteTkv2OrderAPI = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      CompleteTkv2OrderAPIError,
      CompleteTkv2OrderAPIVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    undefined,
    CompleteTkv2OrderAPIError,
    CompleteTkv2OrderAPIVariables
  >({
    mutationFn: (variables: CompleteTkv2OrderAPIVariables) =>
      fetchCompleteTkv2OrderAPI(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type EscalateTkv2OrderAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type EscalateTkv2OrderAPIError = Fetcher.ErrorWrapper<undefined>;

export type EscalateTkv2OrderAPIVariables = {
  body?: Schemas.GeneralRequestType;
  pathParams: EscalateTkv2OrderAPIPathParams;
} & ToolkitContext['fetcherOptions'];

/**
 * Escalates an order.
 */
export const fetchEscalateTkv2OrderAPI = (
  variables: EscalateTkv2OrderAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    undefined,
    EscalateTkv2OrderAPIError,
    Schemas.GeneralRequestType,
    {},
    {},
    EscalateTkv2OrderAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/escalate',
    method: 'post',
    ...variables,
    signal,
  });

/**
 * Escalates an order.
 */
export const useEscalateTkv2OrderAPI = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      EscalateTkv2OrderAPIError,
      EscalateTkv2OrderAPIVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    undefined,
    EscalateTkv2OrderAPIError,
    EscalateTkv2OrderAPIVariables
  >({
    mutationFn: (variables: EscalateTkv2OrderAPIVariables) =>
      fetchEscalateTkv2OrderAPI(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type CancelTkv2OrderAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type CancelTkv2OrderAPIError = Fetcher.ErrorWrapper<undefined>;

export type CancelTkv2OrderAPIVariables = {
  body?: Schemas.GeneralRequestType;
  pathParams: CancelTkv2OrderAPIPathParams;
} & ToolkitContext['fetcherOptions'];

/**
 * Cancels an order.
 */
export const fetchCancelTkv2OrderAPI = (
  variables: CancelTkv2OrderAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    undefined,
    CancelTkv2OrderAPIError,
    Schemas.GeneralRequestType,
    {},
    {},
    CancelTkv2OrderAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/cancel',
    method: 'put',
    ...variables,
    signal,
  });

/**
 * Cancels an order.
 */
export const useCancelTkv2OrderAPI = (
  options?: Omit<
    reactQuery.UseMutationOptions<undefined, CancelTkv2OrderAPIError, CancelTkv2OrderAPIVariables>,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<undefined, CancelTkv2OrderAPIError, CancelTkv2OrderAPIVariables>({
    mutationFn: (variables: CancelTkv2OrderAPIVariables) =>
      fetchCancelTkv2OrderAPI(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type ResumeTkv2OrderAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type ResumeTkv2OrderAPIError = Fetcher.ErrorWrapper<undefined>;

export type ResumeTkv2OrderAPIVariables = {
  body?: Schemas.GeneralRequestType;
  pathParams: ResumeTkv2OrderAPIPathParams;
} & ToolkitContext['fetcherOptions'];

/**
 * Resumes an order.
 */
export const fetchResumeTkv2OrderAPI = (
  variables: ResumeTkv2OrderAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    undefined,
    ResumeTkv2OrderAPIError,
    Schemas.GeneralRequestType,
    {},
    {},
    ResumeTkv2OrderAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/resume',
    method: 'put',
    ...variables,
    signal,
  });

/**
 * Resumes an order.
 */
export const useResumeTkv2OrderAPI = (
  options?: Omit<
    reactQuery.UseMutationOptions<undefined, ResumeTkv2OrderAPIError, ResumeTkv2OrderAPIVariables>,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<undefined, ResumeTkv2OrderAPIError, ResumeTkv2OrderAPIVariables>({
    mutationFn: (variables: ResumeTkv2OrderAPIVariables) =>
      fetchResumeTkv2OrderAPI(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type RescheduleTkv2OrderAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type RescheduleTkv2OrderAPIError = Fetcher.ErrorWrapper<undefined>;

export type RescheduleTkv2OrderAPIVariables = {
  body?: Schemas.GeneralRequestType;
  pathParams: RescheduleTkv2OrderAPIPathParams;
} & ToolkitContext['fetcherOptions'];

/**
 * Reschedules an order.
 */
export const fetchRescheduleTkv2OrderAPI = (
  variables: RescheduleTkv2OrderAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    undefined,
    RescheduleTkv2OrderAPIError,
    Schemas.GeneralRequestType,
    {},
    {},
    RescheduleTkv2OrderAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/reschedule',
    method: 'put',
    ...variables,
    signal,
  });

/**
 * Reschedules an order.
 */
export const useRescheduleTkv2OrderAPI = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      RescheduleTkv2OrderAPIError,
      RescheduleTkv2OrderAPIVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    undefined,
    RescheduleTkv2OrderAPIError,
    RescheduleTkv2OrderAPIVariables
  >({
    mutationFn: (variables: RescheduleTkv2OrderAPIVariables) =>
      fetchRescheduleTkv2OrderAPI(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type UpdateTkv2OrderCompAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
  /**
   * A unique identifier for a 'Comparable'.
   */
  compID: string;
};

export type UpdateTkv2OrderCompAPIError = Fetcher.ErrorWrapper<undefined>;

export type UpdateTkv2OrderCompAPIVariables = {
  body?: Schemas.GeneralRequestType;
  pathParams: UpdateTkv2OrderCompAPIPathParams;
} & ToolkitContext['fetcherOptions'];

/**
 * Updates the details of a single order Comparable.
 */
export const fetchUpdateTkv2OrderCompAPI = (
  variables: UpdateTkv2OrderCompAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    undefined,
    UpdateTkv2OrderCompAPIError,
    Schemas.GeneralRequestType,
    {},
    {},
    UpdateTkv2OrderCompAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/comp/{compID}',
    method: 'put',
    ...variables,
    signal,
  });

/**
 * Updates the details of a single order Comparable.
 */
export const useUpdateTkv2OrderCompAPI = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      UpdateTkv2OrderCompAPIError,
      UpdateTkv2OrderCompAPIVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    undefined,
    UpdateTkv2OrderCompAPIError,
    UpdateTkv2OrderCompAPIVariables
  >({
    mutationFn: (variables: UpdateTkv2OrderCompAPIVariables) =>
      fetchUpdateTkv2OrderCompAPI(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type UpdateCompPropertyDataPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
  /**
   * A unique identifier for a 'Comparable'.
   */
  compID: string;
};

export type UpdateCompPropertyDataError = Fetcher.ErrorWrapper<undefined>;

export type UpdateCompPropertyDataVariables = {
  body?: Schemas.GeneralRequestType;
  pathParams: UpdateCompPropertyDataPathParams;
} & ToolkitContext['fetcherOptions'];

/**
 * Updates the details of a single order Comparable property data.
 */
export const fetchUpdateCompPropertyData = (
  variables: UpdateCompPropertyDataVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    undefined,
    UpdateCompPropertyDataError,
    Schemas.GeneralRequestType,
    {},
    {},
    UpdateCompPropertyDataPathParams
  >({
    url: '/api/v2/orders/{orderID}/compdata/{compID}/propertyData',
    method: 'put',
    ...variables,
    signal,
  });

/**
 * Updates the details of a single order Comparable property data.
 */
export const useUpdateCompPropertyData = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      UpdateCompPropertyDataError,
      UpdateCompPropertyDataVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    undefined,
    UpdateCompPropertyDataError,
    UpdateCompPropertyDataVariables
  >({
    mutationFn: (variables: UpdateCompPropertyDataVariables) =>
      fetchUpdateCompPropertyData(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetCompSequenceAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type GetCompSequenceAPIError = Fetcher.ErrorWrapper<undefined>;

export type GetCompSequenceAPIVariables = {
  pathParams: GetCompSequenceAPIPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchGetCompSequenceAPI = (
  variables: GetCompSequenceAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    GetCompSequenceAPIError,
    undefined,
    {},
    {},
    GetCompSequenceAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/attachments/compSequence',
    method: 'get',
    ...variables,
    signal,
  });

export function getCompSequenceApiQuery(variables: GetCompSequenceAPIVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getCompSequenceApiQuery(variables: GetCompSequenceAPIVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getCompSequenceApiQuery(variables: GetCompSequenceAPIVariables) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v2/orders/{orderID}/attachments/compSequence',
      operationId: 'getCompSequenceAPI',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) => fetchGetCompSequenceAPI(variables, signal),
  };
}

export const useSuspenseGetCompSequenceAPI = <TData = Schemas.Response>(
  variables: GetCompSequenceAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetCompSequenceAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<Schemas.Response, GetCompSequenceAPIError, TData>({
    ...getCompSequenceApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetCompSequenceAPI = <TData = Schemas.Response>(
  variables: GetCompSequenceAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetCompSequenceAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, GetCompSequenceAPIError, TData>({
    ...getCompSequenceApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type PutCompSequenceAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type PutCompSequenceAPIError = Fetcher.ErrorWrapper<undefined>;

export type PutCompSequenceAPIVariables = {
  body?: Schemas.GeneralRequestType;
  pathParams: PutCompSequenceAPIPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchPutCompSequenceAPI = (
  variables: PutCompSequenceAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    PutCompSequenceAPIError,
    Schemas.GeneralRequestType,
    {},
    {},
    PutCompSequenceAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/attachments/compSequence',
    method: 'put',
    ...variables,
    signal,
  });

export const usePutCompSequenceAPI = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      PutCompSequenceAPIError,
      PutCompSequenceAPIVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    Schemas.Response,
    PutCompSequenceAPIError,
    PutCompSequenceAPIVariables
  >({
    mutationFn: (variables: PutCompSequenceAPIVariables) =>
      fetchPutCompSequenceAPI(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetRentalCompSequenceAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type GetRentalCompSequenceAPIError = Fetcher.ErrorWrapper<undefined>;

export type GetRentalCompSequenceAPIVariables = {
  pathParams: GetRentalCompSequenceAPIPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchGetRentalCompSequenceAPI = (
  variables: GetRentalCompSequenceAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    GetRentalCompSequenceAPIError,
    undefined,
    {},
    {},
    GetRentalCompSequenceAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/attachments/rentalCompSequence',
    method: 'get',
    ...variables,
    signal,
  });

export function getRentalCompSequenceApiQuery(variables: GetRentalCompSequenceAPIVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getRentalCompSequenceApiQuery(variables: GetRentalCompSequenceAPIVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getRentalCompSequenceApiQuery(variables: GetRentalCompSequenceAPIVariables) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v2/orders/{orderID}/attachments/rentalCompSequence',
      operationId: 'getRentalCompSequenceAPI',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) => fetchGetRentalCompSequenceAPI(variables, signal),
  };
}

export const useSuspenseGetRentalCompSequenceAPI = <TData = Schemas.Response>(
  variables: GetRentalCompSequenceAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetRentalCompSequenceAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<Schemas.Response, GetRentalCompSequenceAPIError, TData>({
    ...getRentalCompSequenceApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetRentalCompSequenceAPI = <TData = Schemas.Response>(
  variables: GetRentalCompSequenceAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetRentalCompSequenceAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, GetRentalCompSequenceAPIError, TData>({
    ...getRentalCompSequenceApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type PutRentalCompSequenceAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type PutRentalCompSequenceAPIError = Fetcher.ErrorWrapper<undefined>;

export type PutRentalCompSequenceAPIVariables = {
  body?: Schemas.GeneralRequestType;
  pathParams: PutRentalCompSequenceAPIPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchPutRentalCompSequenceAPI = (
  variables: PutRentalCompSequenceAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    PutRentalCompSequenceAPIError,
    Schemas.GeneralRequestType,
    {},
    {},
    PutRentalCompSequenceAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/attachments/rentalCompSequence',
    method: 'put',
    ...variables,
    signal,
  });

export const usePutRentalCompSequenceAPI = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      PutRentalCompSequenceAPIError,
      PutRentalCompSequenceAPIVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    Schemas.Response,
    PutRentalCompSequenceAPIError,
    PutRentalCompSequenceAPIVariables
  >({
    mutationFn: (variables: PutRentalCompSequenceAPIVariables) =>
      fetchPutRentalCompSequenceAPI(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetTkv2AdditionalFeaturesAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type GetTkv2AdditionalFeaturesAPIError = Fetcher.ErrorWrapper<undefined>;

export type GetTkv2AdditionalFeaturesAPIVariables = {
  pathParams: GetTkv2AdditionalFeaturesAPIPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchGetTkv2AdditionalFeaturesAPI = (
  variables: GetTkv2AdditionalFeaturesAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    GetTkv2AdditionalFeaturesAPIError,
    undefined,
    {},
    {},
    GetTkv2AdditionalFeaturesAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/attachments/additionalFeatures',
    method: 'get',
    ...variables,
    signal,
  });

export function getTkv2AdditionalFeaturesApiQuery(
  variables: GetTkv2AdditionalFeaturesAPIVariables,
): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getTkv2AdditionalFeaturesApiQuery(
  variables: GetTkv2AdditionalFeaturesAPIVariables,
): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getTkv2AdditionalFeaturesApiQuery(
  variables: GetTkv2AdditionalFeaturesAPIVariables,
) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v2/orders/{orderID}/attachments/additionalFeatures',
      operationId: 'getTkv2AdditionalFeaturesAPI',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) => fetchGetTkv2AdditionalFeaturesAPI(variables, signal),
  };
}

export const useSuspenseGetTkv2AdditionalFeaturesAPI = <TData = Schemas.Response>(
  variables: GetTkv2AdditionalFeaturesAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetTkv2AdditionalFeaturesAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<Schemas.Response, GetTkv2AdditionalFeaturesAPIError, TData>({
    ...getTkv2AdditionalFeaturesApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetTkv2AdditionalFeaturesAPI = <TData = Schemas.Response>(
  variables: GetTkv2AdditionalFeaturesAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetTkv2AdditionalFeaturesAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, GetTkv2AdditionalFeaturesAPIError, TData>({
    ...getTkv2AdditionalFeaturesApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type PutTkv2AdditionalFeaturesAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type PutTkv2AdditionalFeaturesAPIError = Fetcher.ErrorWrapper<undefined>;

export type PutTkv2AdditionalFeaturesAPIVariables = {
  body?: Schemas.GeneralRequestType;
  pathParams: PutTkv2AdditionalFeaturesAPIPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchPutTkv2AdditionalFeaturesAPI = (
  variables: PutTkv2AdditionalFeaturesAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    PutTkv2AdditionalFeaturesAPIError,
    Schemas.GeneralRequestType,
    {},
    {},
    PutTkv2AdditionalFeaturesAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/attachments/additionalFeatures',
    method: 'put',
    ...variables,
    signal,
  });

export const usePutTkv2AdditionalFeaturesAPI = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      PutTkv2AdditionalFeaturesAPIError,
      PutTkv2AdditionalFeaturesAPIVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    Schemas.Response,
    PutTkv2AdditionalFeaturesAPIError,
    PutTkv2AdditionalFeaturesAPIVariables
  >({
    mutationFn: (variables: PutTkv2AdditionalFeaturesAPIVariables) =>
      fetchPutTkv2AdditionalFeaturesAPI(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetTkv2OrderFeaturesAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type GetTkv2OrderFeaturesAPIError = Fetcher.ErrorWrapper<undefined>;

export type GetTkv2OrderFeaturesAPIVariables = {
  pathParams: GetTkv2OrderFeaturesAPIPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchGetTkv2OrderFeaturesAPI = (
  variables: GetTkv2OrderFeaturesAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    GetTkv2OrderFeaturesAPIError,
    undefined,
    {},
    {},
    GetTkv2OrderFeaturesAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/attachments/orderFeatures',
    method: 'get',
    ...variables,
    signal,
  });

export function getTkv2OrderFeaturesApiQuery(variables: GetTkv2OrderFeaturesAPIVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getTkv2OrderFeaturesApiQuery(variables: GetTkv2OrderFeaturesAPIVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getTkv2OrderFeaturesApiQuery(variables: GetTkv2OrderFeaturesAPIVariables) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v2/orders/{orderID}/attachments/orderFeatures',
      operationId: 'getTkv2OrderFeaturesAPI',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) => fetchGetTkv2OrderFeaturesAPI(variables, signal),
  };
}

export const useSuspenseGetTkv2OrderFeaturesAPI = <TData = Schemas.Response>(
  variables: GetTkv2OrderFeaturesAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetTkv2OrderFeaturesAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<Schemas.Response, GetTkv2OrderFeaturesAPIError, TData>({
    ...getTkv2OrderFeaturesApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetTkv2OrderFeaturesAPI = <TData = Schemas.Response>(
  variables: GetTkv2OrderFeaturesAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetTkv2OrderFeaturesAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, GetTkv2OrderFeaturesAPIError, TData>({
    ...getTkv2OrderFeaturesApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type PutTkv2OrderFeaturesAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type PutTkv2OrderFeaturesAPIError = Fetcher.ErrorWrapper<undefined>;

export type PutTkv2OrderFeaturesAPIVariables = {
  body?: Schemas.GeneralRequestType;
  pathParams: PutTkv2OrderFeaturesAPIPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchPutTkv2OrderFeaturesAPI = (
  variables: PutTkv2OrderFeaturesAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    PutTkv2OrderFeaturesAPIError,
    Schemas.GeneralRequestType,
    {},
    {},
    PutTkv2OrderFeaturesAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/attachments/orderFeatures',
    method: 'put',
    ...variables,
    signal,
  });

export const usePutTkv2OrderFeaturesAPI = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      PutTkv2OrderFeaturesAPIError,
      PutTkv2OrderFeaturesAPIVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    Schemas.Response,
    PutTkv2OrderFeaturesAPIError,
    PutTkv2OrderFeaturesAPIVariables
  >({
    mutationFn: (variables: PutTkv2OrderFeaturesAPIVariables) =>
      fetchPutTkv2OrderFeaturesAPI(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetTkv2AlertsAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type GetTkv2AlertsAPIError = Fetcher.ErrorWrapper<undefined>;

export type GetTkv2AlertsAPIVariables = {
  pathParams: GetTkv2AlertsAPIPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchGetTkv2AlertsAPI = (variables: GetTkv2AlertsAPIVariables, signal?: AbortSignal) =>
  toolkitFetch<
    Schemas.Response,
    GetTkv2AlertsAPIError,
    undefined,
    {},
    {},
    GetTkv2AlertsAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/attachments/alerts',
    method: 'get',
    ...variables,
    signal,
  });

export function getTkv2AlertsApiQuery(variables: GetTkv2AlertsAPIVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getTkv2AlertsApiQuery(variables: GetTkv2AlertsAPIVariables) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v2/orders/{orderID}/attachments/alerts',
      operationId: 'getTkv2AlertsAPI',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) => fetchGetTkv2AlertsAPI(variables, signal),
  };
}

export const useSuspenseGetTkv2AlertsAPI = <TData = Schemas.Response>(
  variables: GetTkv2AlertsAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetTkv2AlertsAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<Schemas.Response, GetTkv2AlertsAPIError, TData>({
    ...getTkv2AlertsApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetTkv2AlertsAPI = <TData = Schemas.Response>(
  variables: GetTkv2AlertsAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetTkv2AlertsAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, GetTkv2AlertsAPIError, TData>({
    ...getTkv2AlertsApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type PutTkv2AlertsAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type PutTkv2AlertsAPIError = Fetcher.ErrorWrapper<undefined>;

export type PutTkv2AlertsAPIVariables = {
  body?: Schemas.GeneralRequestType;
  pathParams: PutTkv2AlertsAPIPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchPutTkv2AlertsAPI = (variables: PutTkv2AlertsAPIVariables, signal?: AbortSignal) =>
  toolkitFetch<
    Schemas.Response,
    PutTkv2AlertsAPIError,
    Schemas.GeneralRequestType,
    {},
    {},
    PutTkv2AlertsAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/attachments/alerts',
    method: 'put',
    ...variables,
    signal,
  });

export const usePutTkv2AlertsAPI = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      PutTkv2AlertsAPIError,
      PutTkv2AlertsAPIVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<Schemas.Response, PutTkv2AlertsAPIError, PutTkv2AlertsAPIVariables>(
    {
      mutationFn: (variables: PutTkv2AlertsAPIVariables) =>
        fetchPutTkv2AlertsAPI(deepMerge(fetcherOptions, variables)),
      ...options,
    },
  );
};

export type GetTkv2PriorSalesAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type GetTkv2PriorSalesAPIError = Fetcher.ErrorWrapper<undefined>;

export type GetTkv2PriorSalesAPIVariables = {
  pathParams: GetTkv2PriorSalesAPIPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchGetTkv2PriorSalesAPI = (
  variables: GetTkv2PriorSalesAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    GetTkv2PriorSalesAPIError,
    undefined,
    {},
    {},
    GetTkv2PriorSalesAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/attachments/priorSales',
    method: 'get',
    ...variables,
    signal,
  });

export function getTkv2PriorSalesApiQuery(variables: GetTkv2PriorSalesAPIVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getTkv2PriorSalesApiQuery(variables: GetTkv2PriorSalesAPIVariables) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v2/orders/{orderID}/attachments/priorSales',
      operationId: 'getTkv2PriorSalesAPI',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) => fetchGetTkv2PriorSalesAPI(variables, signal),
  };
}

export const useSuspenseGetTkv2PriorSalesAPI = <TData = Schemas.Response>(
  variables: GetTkv2PriorSalesAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetTkv2PriorSalesAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<Schemas.Response, GetTkv2PriorSalesAPIError, TData>({
    ...getTkv2PriorSalesApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetTkv2PriorSalesAPI = <TData = Schemas.Response>(
  variables: GetTkv2PriorSalesAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetTkv2PriorSalesAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, GetTkv2PriorSalesAPIError, TData>({
    ...getTkv2PriorSalesApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type PutTkv2PriorSalesAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type PutTkv2PriorSalesAPIError = Fetcher.ErrorWrapper<undefined>;

export type PutTkv2PriorSalesAPIVariables = {
  body?: Schemas.GeneralRequestType;
  pathParams: PutTkv2PriorSalesAPIPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchPutTkv2PriorSalesAPI = (
  variables: PutTkv2PriorSalesAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    PutTkv2PriorSalesAPIError,
    Schemas.GeneralRequestType,
    {},
    {},
    PutTkv2PriorSalesAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/attachments/priorSales',
    method: 'put',
    ...variables,
    signal,
  });

export const usePutTkv2PriorSalesAPI = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      PutTkv2PriorSalesAPIError,
      PutTkv2PriorSalesAPIVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    Schemas.Response,
    PutTkv2PriorSalesAPIError,
    PutTkv2PriorSalesAPIVariables
  >({
    mutationFn: (variables: PutTkv2PriorSalesAPIVariables) =>
      fetchPutTkv2PriorSalesAPI(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetTkv2AnalyticsAttachmentAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type GetTkv2AnalyticsAttachmentAPIError = Fetcher.ErrorWrapper<undefined>;

export type GetTkv2AnalyticsAttachmentAPIVariables = {
  pathParams: GetTkv2AnalyticsAttachmentAPIPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchGetTkv2AnalyticsAttachmentAPI = (
  variables: GetTkv2AnalyticsAttachmentAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    GetTkv2AnalyticsAttachmentAPIError,
    undefined,
    {},
    {},
    GetTkv2AnalyticsAttachmentAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/attachments/analytics',
    method: 'get',
    ...variables,
    signal,
  });

export function getTkv2AnalyticsAttachmentApiQuery(
  variables: GetTkv2AnalyticsAttachmentAPIVariables,
): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getTkv2AnalyticsAttachmentApiQuery(
  variables: GetTkv2AnalyticsAttachmentAPIVariables,
) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v2/orders/{orderID}/attachments/analytics',
      operationId: 'getTkv2AnalyticsAttachmentAPI',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) => fetchGetTkv2AnalyticsAttachmentAPI(variables, signal),
  };
}

export const useSuspenseGetTkv2AnalyticsAttachmentAPI = <TData = Schemas.Response>(
  variables: GetTkv2AnalyticsAttachmentAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetTkv2AnalyticsAttachmentAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<Schemas.Response, GetTkv2AnalyticsAttachmentAPIError, TData>({
    ...getTkv2AnalyticsAttachmentApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetTkv2AnalyticsAttachmentAPI = <TData = Schemas.Response>(
  variables: GetTkv2AnalyticsAttachmentAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetTkv2AnalyticsAttachmentAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, GetTkv2AnalyticsAttachmentAPIError, TData>({
    ...getTkv2AnalyticsAttachmentApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type PutTkv2AnalyticsAttachmentAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type PutTkv2AnalyticsAttachmentAPIError = Fetcher.ErrorWrapper<undefined>;

export type PutTkv2AnalyticsAttachmentAPIVariables = {
  body?: Schemas.GeneralRequestType;
  pathParams: PutTkv2AnalyticsAttachmentAPIPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchPutTkv2AnalyticsAttachmentAPI = (
  variables: PutTkv2AnalyticsAttachmentAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    PutTkv2AnalyticsAttachmentAPIError,
    Schemas.GeneralRequestType,
    {},
    {},
    PutTkv2AnalyticsAttachmentAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/attachments/analytics',
    method: 'put',
    ...variables,
    signal,
  });

export const usePutTkv2AnalyticsAttachmentAPI = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      PutTkv2AnalyticsAttachmentAPIError,
      PutTkv2AnalyticsAttachmentAPIVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    Schemas.Response,
    PutTkv2AnalyticsAttachmentAPIError,
    PutTkv2AnalyticsAttachmentAPIVariables
  >({
    mutationFn: (variables: PutTkv2AnalyticsAttachmentAPIVariables) =>
      fetchPutTkv2AnalyticsAttachmentAPI(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetTkv2CommentaryAttachmentAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type GetTkv2CommentaryAttachmentAPIError = Fetcher.ErrorWrapper<undefined>;

export type GetTkv2CommentaryAttachmentAPIVariables = {
  pathParams: GetTkv2CommentaryAttachmentAPIPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchGetTkv2CommentaryAttachmentAPI = (
  variables: GetTkv2CommentaryAttachmentAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    GetTkv2CommentaryAttachmentAPIError,
    undefined,
    {},
    {},
    GetTkv2CommentaryAttachmentAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/attachments/commentary',
    method: 'get',
    ...variables,
    signal,
  });

export function getTkv2CommentaryAttachmentApiQuery(
  variables: GetTkv2CommentaryAttachmentAPIVariables,
): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getTkv2CommentaryAttachmentApiQuery(
  variables: GetTkv2CommentaryAttachmentAPIVariables,
): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getTkv2CommentaryAttachmentApiQuery(
  variables: GetTkv2CommentaryAttachmentAPIVariables,
) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v2/orders/{orderID}/attachments/commentary',
      operationId: 'getTkv2CommentaryAttachmentAPI',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) => fetchGetTkv2CommentaryAttachmentAPI(variables, signal),
  };
}

export const useSuspenseGetTkv2CommentaryAttachmentAPI = <TData = Schemas.Response>(
  variables: GetTkv2CommentaryAttachmentAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetTkv2CommentaryAttachmentAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<Schemas.Response, GetTkv2CommentaryAttachmentAPIError, TData>({
    ...getTkv2CommentaryAttachmentApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetTkv2CommentaryAttachmentAPI = <TData = Schemas.Response>(
  variables: GetTkv2CommentaryAttachmentAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetTkv2CommentaryAttachmentAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, GetTkv2CommentaryAttachmentAPIError, TData>({
    ...getTkv2CommentaryAttachmentApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type PutTkv2CommentaryAttachmentAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type PutTkv2CommentaryAttachmentAPIError = Fetcher.ErrorWrapper<undefined>;

export type PutTkv2CommentaryAttachmentAPIVariables = {
  body?: Schemas.GeneralRequestType;
  pathParams: PutTkv2CommentaryAttachmentAPIPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchPutTkv2CommentaryAttachmentAPI = (
  variables: PutTkv2CommentaryAttachmentAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    PutTkv2CommentaryAttachmentAPIError,
    Schemas.GeneralRequestType,
    {},
    {},
    PutTkv2CommentaryAttachmentAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/attachments/commentary',
    method: 'put',
    ...variables,
    signal,
  });

export const usePutTkv2CommentaryAttachmentAPI = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      PutTkv2CommentaryAttachmentAPIError,
      PutTkv2CommentaryAttachmentAPIVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    Schemas.Response,
    PutTkv2CommentaryAttachmentAPIError,
    PutTkv2CommentaryAttachmentAPIVariables
  >({
    mutationFn: (variables: PutTkv2CommentaryAttachmentAPIVariables) =>
      fetchPutTkv2CommentaryAttachmentAPI(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetTkv2ReconciliationAttachmentAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type GetTkv2ReconciliationAttachmentAPIError = Fetcher.ErrorWrapper<undefined>;

export type GetTkv2ReconciliationAttachmentAPIVariables = {
  pathParams: GetTkv2ReconciliationAttachmentAPIPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchGetTkv2ReconciliationAttachmentAPI = (
  variables: GetTkv2ReconciliationAttachmentAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    GetTkv2ReconciliationAttachmentAPIError,
    undefined,
    {},
    {},
    GetTkv2ReconciliationAttachmentAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/attachments/reconciliation',
    method: 'get',
    ...variables,
    signal,
  });

export function getTkv2ReconciliationAttachmentApiQuery(
  variables: GetTkv2ReconciliationAttachmentAPIVariables,
): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getTkv2ReconciliationAttachmentApiQuery(
  variables: GetTkv2ReconciliationAttachmentAPIVariables,
): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getTkv2ReconciliationAttachmentApiQuery(
  variables: GetTkv2ReconciliationAttachmentAPIVariables,
) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v2/orders/{orderID}/attachments/reconciliation',
      operationId: 'getTkv2ReconciliationAttachmentAPI',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) =>
      fetchGetTkv2ReconciliationAttachmentAPI(variables, signal),
  };
}

export const useSuspenseGetTkv2ReconciliationAttachmentAPI = <TData = Schemas.Response>(
  variables: GetTkv2ReconciliationAttachmentAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetTkv2ReconciliationAttachmentAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<
    Schemas.Response,
    GetTkv2ReconciliationAttachmentAPIError,
    TData
  >({
    ...getTkv2ReconciliationAttachmentApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetTkv2ReconciliationAttachmentAPI = <TData = Schemas.Response>(
  variables: GetTkv2ReconciliationAttachmentAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetTkv2ReconciliationAttachmentAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, GetTkv2ReconciliationAttachmentAPIError, TData>({
    ...getTkv2ReconciliationAttachmentApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type PutTkv2ReconciliationAttachmentAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type PutTkv2ReconciliationAttachmentAPIError = Fetcher.ErrorWrapper<undefined>;

export type PutTkv2ReconciliationAttachmentAPIVariables = {
  body?: Schemas.GeneralRequestType;
  pathParams: PutTkv2ReconciliationAttachmentAPIPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchPutTkv2ReconciliationAttachmentAPI = (
  variables: PutTkv2ReconciliationAttachmentAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    PutTkv2ReconciliationAttachmentAPIError,
    Schemas.GeneralRequestType,
    {},
    {},
    PutTkv2ReconciliationAttachmentAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/attachments/reconciliation',
    method: 'put',
    ...variables,
    signal,
  });

export const usePutTkv2ReconciliationAttachmentAPI = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      PutTkv2ReconciliationAttachmentAPIError,
      PutTkv2ReconciliationAttachmentAPIVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    Schemas.Response,
    PutTkv2ReconciliationAttachmentAPIError,
    PutTkv2ReconciliationAttachmentAPIVariables
  >({
    mutationFn: (variables: PutTkv2ReconciliationAttachmentAPIVariables) =>
      fetchPutTkv2ReconciliationAttachmentAPI(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetTkv2UnitRateModelsAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type GetTkv2UnitRateModelsAPIError = Fetcher.ErrorWrapper<undefined>;

export type GetTkv2UnitRateModelsAPIVariables = {
  pathParams: GetTkv2UnitRateModelsAPIPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchGetTkv2UnitRateModelsAPI = (
  variables: GetTkv2UnitRateModelsAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    GetTkv2UnitRateModelsAPIError,
    undefined,
    {},
    {},
    GetTkv2UnitRateModelsAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/attachments/unitRateModels',
    method: 'get',
    ...variables,
    signal,
  });

export function getTkv2UnitRateModelsApiQuery(variables: GetTkv2UnitRateModelsAPIVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getTkv2UnitRateModelsApiQuery(variables: GetTkv2UnitRateModelsAPIVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getTkv2UnitRateModelsApiQuery(variables: GetTkv2UnitRateModelsAPIVariables) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v2/orders/{orderID}/attachments/unitRateModels',
      operationId: 'getTkv2UnitRateModelsAPI',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) => fetchGetTkv2UnitRateModelsAPI(variables, signal),
  };
}

export const useSuspenseGetTkv2UnitRateModelsAPI = <TData = Schemas.Response>(
  variables: GetTkv2UnitRateModelsAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetTkv2UnitRateModelsAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<Schemas.Response, GetTkv2UnitRateModelsAPIError, TData>({
    ...getTkv2UnitRateModelsApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetTkv2UnitRateModelsAPI = <TData = Schemas.Response>(
  variables: GetTkv2UnitRateModelsAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetTkv2UnitRateModelsAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, GetTkv2UnitRateModelsAPIError, TData>({
    ...getTkv2UnitRateModelsApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type PostTkv2UnitRateModelsAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type PostTkv2UnitRateModelsAPIError = Fetcher.ErrorWrapper<undefined>;

export type PostTkv2UnitRateModelsAPIVariables = {
  pathParams: PostTkv2UnitRateModelsAPIPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchPostTkv2UnitRateModelsAPI = (
  variables: PostTkv2UnitRateModelsAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    PostTkv2UnitRateModelsAPIError,
    undefined,
    {},
    {},
    PostTkv2UnitRateModelsAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/attachments/unitRateModels',
    method: 'post',
    ...variables,
    signal,
  });

export const usePostTkv2UnitRateModelsAPI = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      PostTkv2UnitRateModelsAPIError,
      PostTkv2UnitRateModelsAPIVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    Schemas.Response,
    PostTkv2UnitRateModelsAPIError,
    PostTkv2UnitRateModelsAPIVariables
  >({
    mutationFn: (variables: PostTkv2UnitRateModelsAPIVariables) =>
      fetchPostTkv2UnitRateModelsAPI(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetVacantLandSalesPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type GetVacantLandSalesError = Fetcher.ErrorWrapper<undefined>;

export type GetVacantLandSalesVariables = {
  pathParams: GetVacantLandSalesPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchGetVacantLandSales = (
  variables: GetVacantLandSalesVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    GetVacantLandSalesError,
    undefined,
    {},
    {},
    GetVacantLandSalesPathParams
  >({
    url: '/api/v2/orders/{orderID}/attachments/vacantLandSales',
    method: 'get',
    ...variables,
    signal,
  });

export function getVacantLandSalesQuery(variables: GetVacantLandSalesVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getVacantLandSalesQuery(variables: GetVacantLandSalesVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getVacantLandSalesQuery(variables: GetVacantLandSalesVariables) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v2/orders/{orderID}/attachments/vacantLandSales',
      operationId: 'getVacantLandSales',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) => fetchGetVacantLandSales(variables, signal),
  };
}

export const useSuspenseGetVacantLandSales = <TData = Schemas.Response>(
  variables: GetVacantLandSalesVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetVacantLandSalesError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<Schemas.Response, GetVacantLandSalesError, TData>({
    ...getVacantLandSalesQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetVacantLandSales = <TData = Schemas.Response>(
  variables: GetVacantLandSalesVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetVacantLandSalesError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, GetVacantLandSalesError, TData>({
    ...getVacantLandSalesQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type PostVacantLandSalesPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type PostVacantLandSalesError = Fetcher.ErrorWrapper<undefined>;

export type PostVacantLandSalesVariables = {
  pathParams: PostVacantLandSalesPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchPostVacantLandSales = (
  variables: PostVacantLandSalesVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    PostVacantLandSalesError,
    undefined,
    {},
    {},
    PostVacantLandSalesPathParams
  >({
    url: '/api/v2/orders/{orderID}/attachments/vacantLandSales',
    method: 'post',
    ...variables,
    signal,
  });

export const usePostVacantLandSales = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      PostVacantLandSalesError,
      PostVacantLandSalesVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    Schemas.Response,
    PostVacantLandSalesError,
    PostVacantLandSalesVariables
  >({
    mutationFn: (variables: PostVacantLandSalesVariables) =>
      fetchPostVacantLandSales(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetTkv2OrderSubjectAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type GetTkv2OrderSubjectAPIError = Fetcher.ErrorWrapper<undefined>;

export type GetTkv2OrderSubjectAPIVariables = {
  pathParams: GetTkv2OrderSubjectAPIPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchGetTkv2OrderSubjectAPI = (
  variables: GetTkv2OrderSubjectAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    GetTkv2OrderSubjectAPIError,
    undefined,
    {},
    {},
    GetTkv2OrderSubjectAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/attachments/subject',
    method: 'get',
    ...variables,
    signal,
  });

export function getTkv2OrderSubjectApiQuery(variables: GetTkv2OrderSubjectAPIVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getTkv2OrderSubjectApiQuery(variables: GetTkv2OrderSubjectAPIVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getTkv2OrderSubjectApiQuery(variables: GetTkv2OrderSubjectAPIVariables) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v2/orders/{orderID}/attachments/subject',
      operationId: 'getTkv2OrderSubjectAPI',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) => fetchGetTkv2OrderSubjectAPI(variables, signal),
  };
}

export const useSuspenseGetTkv2OrderSubjectAPI = <TData = Schemas.Response>(
  variables: GetTkv2OrderSubjectAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetTkv2OrderSubjectAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<Schemas.Response, GetTkv2OrderSubjectAPIError, TData>({
    ...getTkv2OrderSubjectApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetTkv2OrderSubjectAPI = <TData = Schemas.Response>(
  variables: GetTkv2OrderSubjectAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetTkv2OrderSubjectAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, GetTkv2OrderSubjectAPIError, TData>({
    ...getTkv2OrderSubjectApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type UpdateTkv2OrderSubjectAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type UpdateTkv2OrderSubjectAPIError = Fetcher.ErrorWrapper<undefined>;

export type UpdateTkv2OrderSubjectAPIVariables = {
  body?: Schemas.GeneralRequestType;
  pathParams: UpdateTkv2OrderSubjectAPIPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchUpdateTkv2OrderSubjectAPI = (
  variables: UpdateTkv2OrderSubjectAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    UpdateTkv2OrderSubjectAPIError,
    Schemas.GeneralRequestType,
    {},
    {},
    UpdateTkv2OrderSubjectAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/attachments/subject',
    method: 'put',
    ...variables,
    signal,
  });

export const useUpdateTkv2OrderSubjectAPI = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      UpdateTkv2OrderSubjectAPIError,
      UpdateTkv2OrderSubjectAPIVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    Schemas.Response,
    UpdateTkv2OrderSubjectAPIError,
    UpdateTkv2OrderSubjectAPIVariables
  >({
    mutationFn: (variables: UpdateTkv2OrderSubjectAPIVariables) =>
      fetchUpdateTkv2OrderSubjectAPI(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetQaAttemptPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type GetQaAttemptError = Fetcher.ErrorWrapper<undefined>;

export type GetQaAttemptVariables = {
  pathParams: GetQaAttemptPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchGetQaAttempt = (variables: GetQaAttemptVariables, signal?: AbortSignal) =>
  toolkitFetch<Schemas.Response, GetQaAttemptError, undefined, {}, {}, GetQaAttemptPathParams>({
    url: '/api/v2/orders/{orderID}/qaAttempts',
    method: 'get',
    ...variables,
    signal,
  });

export function getQaAttemptQuery(variables: GetQaAttemptVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getQaAttemptQuery(variables: GetQaAttemptVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getQaAttemptQuery(variables: GetQaAttemptVariables) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v2/orders/{orderID}/qaAttempts',
      operationId: 'getQaAttempt',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) => fetchGetQaAttempt(variables, signal),
  };
}

export const useSuspenseGetQaAttempt = <TData = Schemas.Response>(
  variables: GetQaAttemptVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetQaAttemptError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<Schemas.Response, GetQaAttemptError, TData>({
    ...getQaAttemptQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetQaAttempt = <TData = Schemas.Response>(
  variables: GetQaAttemptVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetQaAttemptError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, GetQaAttemptError, TData>({
    ...getQaAttemptQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type PostQaAttemptPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type PostQaAttemptError = Fetcher.ErrorWrapper<undefined>;

export type PostQaAttemptVariables = {
  body?: Schemas.GeneralRequestType;
  pathParams: PostQaAttemptPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchPostQaAttempt = (variables: PostQaAttemptVariables, signal?: AbortSignal) =>
  toolkitFetch<
    undefined,
    PostQaAttemptError,
    Schemas.GeneralRequestType,
    {},
    {},
    PostQaAttemptPathParams
  >({
    url: '/api/v2/orders/{orderID}/qaAttempts',
    method: 'post',
    ...variables,
    signal,
  });

export const usePostQaAttempt = (
  options?: Omit<
    reactQuery.UseMutationOptions<undefined, PostQaAttemptError, PostQaAttemptVariables>,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<undefined, PostQaAttemptError, PostQaAttemptVariables>({
    mutationFn: (variables: PostQaAttemptVariables) =>
      fetchPostQaAttempt(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetSubjectDataPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type GetSubjectDataError = Fetcher.ErrorWrapper<undefined>;

export type GetSubjectDataVariables = {
  pathParams: GetSubjectDataPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchGetSubjectData = (variables: GetSubjectDataVariables, signal?: AbortSignal) =>
  toolkitFetch<Schemas.Response, GetSubjectDataError, undefined, {}, {}, GetSubjectDataPathParams>({
    url: '/api/v2/orders/{orderID}/attachments/subjectdata',
    method: 'get',
    ...variables,
    signal,
  });

export function getSubjectDataQuery(variables: GetSubjectDataVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getSubjectDataQuery(variables: GetSubjectDataVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getSubjectDataQuery(variables: GetSubjectDataVariables) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v2/orders/{orderID}/attachments/subjectdata',
      operationId: 'getSubjectData',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) => fetchGetSubjectData(variables, signal),
  };
}

export const useSuspenseGetSubjectData = <TData = Schemas.Response>(
  variables: GetSubjectDataVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetSubjectDataError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<Schemas.Response, GetSubjectDataError, TData>({
    ...getSubjectDataQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetSubjectData = <TData = Schemas.Response>(
  variables: GetSubjectDataVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetSubjectDataError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, GetSubjectDataError, TData>({
    ...getSubjectDataQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type UpdateSubjectEditableDataPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type UpdateSubjectEditableDataError = Fetcher.ErrorWrapper<undefined>;

export type UpdateSubjectEditableDataVariables = {
  body?: Schemas.GeneralRequestType;
  pathParams: UpdateSubjectEditableDataPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchUpdateSubjectEditableData = (
  variables: UpdateSubjectEditableDataVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    UpdateSubjectEditableDataError,
    Schemas.GeneralRequestType,
    {},
    {},
    UpdateSubjectEditableDataPathParams
  >({
    url: '/api/v2/orders/{orderID}/attachments/subjectdata',
    method: 'put',
    ...variables,
    signal,
  });

export const useUpdateSubjectEditableData = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      UpdateSubjectEditableDataError,
      UpdateSubjectEditableDataVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    Schemas.Response,
    UpdateSubjectEditableDataError,
    UpdateSubjectEditableDataVariables
  >({
    mutationFn: (variables: UpdateSubjectEditableDataVariables) =>
      fetchUpdateSubjectEditableData(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type UpdateSubjectPropertyDataPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type UpdateSubjectPropertyDataError = Fetcher.ErrorWrapper<undefined>;

export type UpdateSubjectPropertyDataVariables = {
  body?: Schemas.GeneralRequestType;
  pathParams: UpdateSubjectPropertyDataPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchUpdateSubjectPropertyData = (
  variables: UpdateSubjectPropertyDataVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    UpdateSubjectPropertyDataError,
    Schemas.GeneralRequestType,
    {},
    {},
    UpdateSubjectPropertyDataPathParams
  >({
    url: '/api/v2/orders/{orderID}/attachments/subjectdata/propertyData',
    method: 'put',
    ...variables,
    signal,
  });

export const useUpdateSubjectPropertyData = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      UpdateSubjectPropertyDataError,
      UpdateSubjectPropertyDataVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    Schemas.Response,
    UpdateSubjectPropertyDataError,
    UpdateSubjectPropertyDataVariables
  >({
    mutationFn: (variables: UpdateSubjectPropertyDataVariables) =>
      fetchUpdateSubjectPropertyData(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetTkv2CompsAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type GetTkv2CompsAPIError = Fetcher.ErrorWrapper<undefined>;

export type GetTkv2CompsAPIVariables = {
  pathParams: GetTkv2CompsAPIPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchGetTkv2CompsAPI = (variables: GetTkv2CompsAPIVariables, signal?: AbortSignal) =>
  toolkitFetch<
    Schemas.Response,
    GetTkv2CompsAPIError,
    undefined,
    {},
    {},
    GetTkv2CompsAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/attachments/comps',
    method: 'get',
    ...variables,
    signal,
  });

export function getTkv2CompsApiQuery(variables: GetTkv2CompsAPIVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getTkv2CompsApiQuery(variables: GetTkv2CompsAPIVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getTkv2CompsApiQuery(variables: GetTkv2CompsAPIVariables) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v2/orders/{orderID}/attachments/comps',
      operationId: 'getTkv2CompsAPI',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) => fetchGetTkv2CompsAPI(variables, signal),
  };
}

export const useSuspenseGetTkv2CompsAPI = <TData = Schemas.Response>(
  variables: GetTkv2CompsAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetTkv2CompsAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<Schemas.Response, GetTkv2CompsAPIError, TData>({
    ...getTkv2CompsApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetTkv2CompsAPI = <TData = Schemas.Response>(
  variables: GetTkv2CompsAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetTkv2CompsAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, GetTkv2CompsAPIError, TData>({
    ...getTkv2CompsApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type PostTkv2CompsAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type PostTkv2CompsAPIError = Fetcher.ErrorWrapper<undefined>;

export type PostTkv2CompsAPIVariables = {
  body?: Schemas.GeneralRequestType;
  pathParams: PostTkv2CompsAPIPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchPostTkv2CompsAPI = (variables: PostTkv2CompsAPIVariables, signal?: AbortSignal) =>
  toolkitFetch<
    Schemas.Response,
    PostTkv2CompsAPIError,
    Schemas.GeneralRequestType,
    {},
    {},
    PostTkv2CompsAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/attachments/comps',
    method: 'post',
    ...variables,
    signal,
  });

export const usePostTkv2CompsAPI = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      PostTkv2CompsAPIError,
      PostTkv2CompsAPIVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<Schemas.Response, PostTkv2CompsAPIError, PostTkv2CompsAPIVariables>(
    {
      mutationFn: (variables: PostTkv2CompsAPIVariables) =>
        fetchPostTkv2CompsAPI(deepMerge(fetcherOptions, variables)),
      ...options,
    },
  );
};

export type GetAllCompDataPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type GetAllCompDataError = Fetcher.ErrorWrapper<undefined>;

export type GetAllCompDataVariables = {
  pathParams: GetAllCompDataPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchGetAllCompData = (variables: GetAllCompDataVariables, signal?: AbortSignal) =>
  toolkitFetch<Schemas.Response, GetAllCompDataError, undefined, {}, {}, GetAllCompDataPathParams>({
    url: '/api/v2/orders/{orderID}/attachments/compdata',
    method: 'get',
    ...variables,
    signal,
  });

export function getAllCompDataQuery(variables: GetAllCompDataVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getAllCompDataQuery(variables: GetAllCompDataVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getAllCompDataQuery(variables: GetAllCompDataVariables) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v2/orders/{orderID}/attachments/compdata',
      operationId: 'getAllCompData',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) => fetchGetAllCompData(variables, signal),
  };
}

export const useSuspenseGetAllCompData = <TData = Schemas.Response>(
  variables: GetAllCompDataVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetAllCompDataError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<Schemas.Response, GetAllCompDataError, TData>({
    ...getAllCompDataQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetAllCompData = <TData = Schemas.Response>(
  variables: GetAllCompDataVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetAllCompDataError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, GetAllCompDataError, TData>({
    ...getAllCompDataQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type CreateCompDataPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type CreateCompDataError = Fetcher.ErrorWrapper<undefined>;

export type CreateCompDataVariables = {
  body?: Schemas.GeneralRequestType;
  pathParams: CreateCompDataPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchCreateCompData = (variables: CreateCompDataVariables, signal?: AbortSignal) =>
  toolkitFetch<
    Schemas.Response,
    CreateCompDataError,
    Schemas.GeneralRequestType,
    {},
    {},
    CreateCompDataPathParams
  >({
    url: '/api/v2/orders/{orderID}/attachments/compdata',
    method: 'post',
    ...variables,
    signal,
  });

export const useCreateCompData = (
  options?: Omit<
    reactQuery.UseMutationOptions<Schemas.Response, CreateCompDataError, CreateCompDataVariables>,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<Schemas.Response, CreateCompDataError, CreateCompDataVariables>({
    mutationFn: (variables: CreateCompDataVariables) =>
      fetchCreateCompData(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type PostTkv2XmlAttachmentAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type PostTkv2XmlAttachmentAPIError = Fetcher.ErrorWrapper<undefined>;

export type PostTkv2XmlAttachmentAPIVariables = {
  body?: Schemas.GeneralRequestType;
  pathParams: PostTkv2XmlAttachmentAPIPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchPostTkv2XmlAttachmentAPI = (
  variables: PostTkv2XmlAttachmentAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    PostTkv2XmlAttachmentAPIError,
    Schemas.GeneralRequestType,
    {},
    {},
    PostTkv2XmlAttachmentAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/attachments/xml',
    method: 'post',
    ...variables,
    signal,
  });

export const usePostTkv2XmlAttachmentAPI = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      PostTkv2XmlAttachmentAPIError,
      PostTkv2XmlAttachmentAPIVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    Schemas.Response,
    PostTkv2XmlAttachmentAPIError,
    PostTkv2XmlAttachmentAPIVariables
  >({
    mutationFn: (variables: PostTkv2XmlAttachmentAPIVariables) =>
      fetchPostTkv2XmlAttachmentAPI(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetReportAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type GetReportAPIError = Fetcher.ErrorWrapper<undefined>;

export type GetReportAPIVariables = {
  pathParams: GetReportAPIPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchGetReportAPI = (variables: GetReportAPIVariables, signal?: AbortSignal) =>
  toolkitFetch<Schemas.Response, GetReportAPIError, undefined, {}, {}, GetReportAPIPathParams>({
    url: '/api/v2/orders/{orderID}/attachments/report',
    method: 'get',
    ...variables,
    signal,
  });

export function getReportApiQuery(variables: GetReportAPIVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getReportApiQuery(variables: GetReportAPIVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getReportApiQuery(variables: GetReportAPIVariables) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v2/orders/{orderID}/attachments/report',
      operationId: 'getReportAPI',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) => fetchGetReportAPI(variables, signal),
  };
}

export const useSuspenseGetReportAPI = <TData = Schemas.Response>(
  variables: GetReportAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetReportAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<Schemas.Response, GetReportAPIError, TData>({
    ...getReportApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetReportAPI = <TData = Schemas.Response>(
  variables: GetReportAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetReportAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, GetReportAPIError, TData>({
    ...getReportApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type PostReportAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type PostReportAPIError = Fetcher.ErrorWrapper<undefined>;

export type PostReportAPIVariables = {
  body?: Schemas.GeneralRequestType;
  pathParams: PostReportAPIPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchPostReportAPI = (variables: PostReportAPIVariables, signal?: AbortSignal) =>
  toolkitFetch<
    Schemas.Response,
    PostReportAPIError,
    Schemas.GeneralRequestType,
    {},
    {},
    PostReportAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/attachments/report',
    method: 'post',
    ...variables,
    signal,
  });

export const usePostReportAPI = (
  options?: Omit<
    reactQuery.UseMutationOptions<Schemas.Response, PostReportAPIError, PostReportAPIVariables>,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<Schemas.Response, PostReportAPIError, PostReportAPIVariables>({
    mutationFn: (variables: PostReportAPIVariables) =>
      fetchPostReportAPI(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type PostReportSignatureAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type PostReportSignatureAPIError = Fetcher.ErrorWrapper<undefined>;

export type PostReportSignatureAPIVariables = {
  body?: Schemas.GeneralRequestType;
  pathParams: PostReportSignatureAPIPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchPostReportSignatureAPI = (
  variables: PostReportSignatureAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    PostReportSignatureAPIError,
    Schemas.GeneralRequestType,
    {},
    {},
    PostReportSignatureAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/attachments/reportSignature',
    method: 'post',
    ...variables,
    signal,
  });

export const usePostReportSignatureAPI = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      PostReportSignatureAPIError,
      PostReportSignatureAPIVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    Schemas.Response,
    PostReportSignatureAPIError,
    PostReportSignatureAPIVariables
  >({
    mutationFn: (variables: PostReportSignatureAPIVariables) =>
      fetchPostReportSignatureAPI(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetMarketAdjustmentsPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type GetMarketAdjustmentsError = Fetcher.ErrorWrapper<undefined>;

export type GetMarketAdjustmentsVariables = {
  pathParams: GetMarketAdjustmentsPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchGetMarketAdjustments = (
  variables: GetMarketAdjustmentsVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    GetMarketAdjustmentsError,
    undefined,
    {},
    {},
    GetMarketAdjustmentsPathParams
  >({
    url: '/api/v2/orders/{orderID}/attachments/marketAdjustments',
    method: 'get',
    ...variables,
    signal,
  });

export function getMarketAdjustmentsQuery(variables: GetMarketAdjustmentsVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getMarketAdjustmentsQuery(variables: GetMarketAdjustmentsVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getMarketAdjustmentsQuery(variables: GetMarketAdjustmentsVariables) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v2/orders/{orderID}/attachments/marketAdjustments',
      operationId: 'getMarketAdjustments',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) => fetchGetMarketAdjustments(variables, signal),
  };
}

export const useSuspenseGetMarketAdjustments = <TData = Schemas.Response>(
  variables: GetMarketAdjustmentsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetMarketAdjustmentsError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<Schemas.Response, GetMarketAdjustmentsError, TData>({
    ...getMarketAdjustmentsQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetMarketAdjustments = <TData = Schemas.Response>(
  variables: GetMarketAdjustmentsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetMarketAdjustmentsError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, GetMarketAdjustmentsError, TData>({
    ...getMarketAdjustmentsQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type PutMarketAdjustmentsPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type PutMarketAdjustmentsError = Fetcher.ErrorWrapper<undefined>;

export type PutMarketAdjustmentsVariables = {
  body?: Schemas.Request;
  pathParams: PutMarketAdjustmentsPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchPutMarketAdjustments = (
  variables: PutMarketAdjustmentsVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    PutMarketAdjustmentsError,
    Schemas.Request,
    {},
    {},
    PutMarketAdjustmentsPathParams
  >({
    url: '/api/v2/orders/{orderID}/attachments/marketAdjustments',
    method: 'put',
    ...variables,
    signal,
  });

export const usePutMarketAdjustments = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      PutMarketAdjustmentsError,
      PutMarketAdjustmentsVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    Schemas.Response,
    PutMarketAdjustmentsError,
    PutMarketAdjustmentsVariables
  >({
    mutationFn: (variables: PutMarketAdjustmentsVariables) =>
      fetchPutMarketAdjustments(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetTkv2MarketConditionsAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type GetTkv2MarketConditionsAPIError = Fetcher.ErrorWrapper<undefined>;

export type GetTkv2MarketConditionsAPIVariables = {
  pathParams: GetTkv2MarketConditionsAPIPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchGetTkv2MarketConditionsAPI = (
  variables: GetTkv2MarketConditionsAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    GetTkv2MarketConditionsAPIError,
    undefined,
    {},
    {},
    GetTkv2MarketConditionsAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/attachments/marketConditions',
    method: 'get',
    ...variables,
    signal,
  });

export function getTkv2MarketConditionsApiQuery(variables: GetTkv2MarketConditionsAPIVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getTkv2MarketConditionsApiQuery(variables: GetTkv2MarketConditionsAPIVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getTkv2MarketConditionsApiQuery(variables: GetTkv2MarketConditionsAPIVariables) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v2/orders/{orderID}/attachments/marketConditions',
      operationId: 'getTkv2MarketConditionsAPI',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) => fetchGetTkv2MarketConditionsAPI(variables, signal),
  };
}

export const useSuspenseGetTkv2MarketConditionsAPI = <TData = Schemas.Response>(
  variables: GetTkv2MarketConditionsAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetTkv2MarketConditionsAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<Schemas.Response, GetTkv2MarketConditionsAPIError, TData>({
    ...getTkv2MarketConditionsApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetTkv2MarketConditionsAPI = <TData = Schemas.Response>(
  variables: GetTkv2MarketConditionsAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetTkv2MarketConditionsAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, GetTkv2MarketConditionsAPIError, TData>({
    ...getTkv2MarketConditionsApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type UpdateTkv2MarketConditionsAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type UpdateTkv2MarketConditionsAPIError = Fetcher.ErrorWrapper<undefined>;

export type UpdateTkv2MarketConditionsAPIVariables = {
  body?: Schemas.Request;
  pathParams: UpdateTkv2MarketConditionsAPIPathParams;
} & ToolkitContext['fetcherOptions'];

export const fetchUpdateTkv2MarketConditionsAPI = (
  variables: UpdateTkv2MarketConditionsAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    UpdateTkv2MarketConditionsAPIError,
    Schemas.Request,
    {},
    {},
    UpdateTkv2MarketConditionsAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/attachments/marketConditions',
    method: 'put',
    ...variables,
    signal,
  });

export const useUpdateTkv2MarketConditionsAPI = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      UpdateTkv2MarketConditionsAPIError,
      UpdateTkv2MarketConditionsAPIVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    Schemas.Response,
    UpdateTkv2MarketConditionsAPIError,
    UpdateTkv2MarketConditionsAPIVariables
  >({
    mutationFn: (variables: UpdateTkv2MarketConditionsAPIVariables) =>
      fetchUpdateTkv2MarketConditionsAPI(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetTkv2PublicRecordAPIPathParams = {
  orderID: string;
};

export type GetTkv2PublicRecordAPIError = Fetcher.ErrorWrapper<undefined>;

export type GetTkv2PublicRecordAPIVariables = {
  pathParams: GetTkv2PublicRecordAPIPathParams;
} & ToolkitContext['fetcherOptions'];

/**
 * Gets the details of a single instance of a `Public Record`
 */
export const fetchGetTkv2PublicRecordAPI = (
  variables: GetTkv2PublicRecordAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    GetTkv2PublicRecordAPIError,
    undefined,
    {},
    {},
    GetTkv2PublicRecordAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/publicRecord',
    method: 'get',
    ...variables,
    signal,
  });

/**
 * Gets the details of a single instance of a `Public Record`
 */
export function getTkv2PublicRecordApiQuery(variables: GetTkv2PublicRecordAPIVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getTkv2PublicRecordApiQuery(variables: GetTkv2PublicRecordAPIVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getTkv2PublicRecordApiQuery(variables: GetTkv2PublicRecordAPIVariables) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v2/orders/{orderID}/publicRecord',
      operationId: 'getTkv2PublicRecordAPI',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) => fetchGetTkv2PublicRecordAPI(variables, signal),
  };
}

/**
 * Gets the details of a single instance of a `Public Record`
 */
export const useSuspenseGetTkv2PublicRecordAPI = <TData = Schemas.Response>(
  variables: GetTkv2PublicRecordAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetTkv2PublicRecordAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<Schemas.Response, GetTkv2PublicRecordAPIError, TData>({
    ...getTkv2PublicRecordApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

/**
 * Gets the details of a single instance of a `Public Record`
 */
export const useGetTkv2PublicRecordAPI = <TData = Schemas.Response>(
  variables: GetTkv2PublicRecordAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetTkv2PublicRecordAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, GetTkv2PublicRecordAPIError, TData>({
    ...getTkv2PublicRecordApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type GetTkv2PublicRecordsAPIPathParams = {
  orderID: string;
};

export type GetTkv2PublicRecordsAPIQueryParams = {
  addresses: string;
};

export type GetTkv2PublicRecordsAPIError = Fetcher.ErrorWrapper<undefined>;

export type GetTkv2PublicRecordsAPIVariables = {
  pathParams: GetTkv2PublicRecordsAPIPathParams;
  queryParams: GetTkv2PublicRecordsAPIQueryParams;
} & ToolkitContext['fetcherOptions'];

/**
 * Gets the details of multiple instances of `Public Record`
 */
export const fetchGetTkv2PublicRecordsAPI = (
  variables: GetTkv2PublicRecordsAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    GetTkv2PublicRecordsAPIError,
    undefined,
    {},
    GetTkv2PublicRecordsAPIQueryParams,
    GetTkv2PublicRecordsAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/publicRecords',
    method: 'get',
    ...variables,
    signal,
  });

/**
 * Gets the details of multiple instances of `Public Record`
 */
export function getTkv2PublicRecordsApiQuery(variables: GetTkv2PublicRecordsAPIVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getTkv2PublicRecordsApiQuery(variables: GetTkv2PublicRecordsAPIVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getTkv2PublicRecordsApiQuery(variables: GetTkv2PublicRecordsAPIVariables) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v2/orders/{orderID}/publicRecords',
      operationId: 'getTkv2PublicRecordsAPI',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) => fetchGetTkv2PublicRecordsAPI(variables, signal),
  };
}

/**
 * Gets the details of multiple instances of `Public Record`
 */
export const useSuspenseGetTkv2PublicRecordsAPI = <TData = Schemas.Response>(
  variables: GetTkv2PublicRecordsAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetTkv2PublicRecordsAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<Schemas.Response, GetTkv2PublicRecordsAPIError, TData>({
    ...getTkv2PublicRecordsApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

/**
 * Gets the details of multiple instances of `Public Record`
 */
export const useGetTkv2PublicRecordsAPI = <TData = Schemas.Response>(
  variables: GetTkv2PublicRecordsAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetTkv2PublicRecordsAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, GetTkv2PublicRecordsAPIError, TData>({
    ...getTkv2PublicRecordsApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type GetMLSDataAPIError = Fetcher.ErrorWrapper<undefined>;

export type GetMLSDataAPIVariables = ToolkitContext['fetcherOptions'];

/**
 * Gets the MLS listing data for a given property.
 */
export const fetchGetMLSDataAPI = (variables: GetMLSDataAPIVariables, signal?: AbortSignal) =>
  toolkitFetch<Schemas.Response, GetMLSDataAPIError, undefined, {}, {}, {}>({
    url: '/api/v2/mls',
    method: 'get',
    ...variables,
    signal,
  });

/**
 * Gets the MLS listing data for a given property.
 */
export function getMlsDataApiQuery(variables: GetMLSDataAPIVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getMlsDataApiQuery(variables: GetMLSDataAPIVariables) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v2/mls',
      operationId: 'getMLSDataAPI',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) => fetchGetMLSDataAPI(variables, signal),
  };
}

/**
 * Gets the MLS listing data for a given property.
 */
export const useSuspenseGetMLSDataAPI = <TData = Schemas.Response>(
  variables: GetMLSDataAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetMLSDataAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<Schemas.Response, GetMLSDataAPIError, TData>({
    ...getMlsDataApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

/**
 * Gets the MLS listing data for a given property.
 */
export const useGetMLSDataAPI = <TData = Schemas.Response>(
  variables: GetMLSDataAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetMLSDataAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, GetMLSDataAPIError, TData>({
    ...getMlsDataApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type GetTkv2OrderPairedCostSettingsAPIPathParams = {
  orderID: string;
};

export type GetTkv2OrderPairedCostSettingsAPIError = Fetcher.ErrorWrapper<undefined>;

export type GetTkv2OrderPairedCostSettingsAPIVariables = {
  pathParams: GetTkv2OrderPairedCostSettingsAPIPathParams;
} & ToolkitContext['fetcherOptions'];

/**
 * Gets the paired cost settings used by this order. If this order does not yet have any settings, the account defaults will be used and stored in the order. (This is idempotent.)
 */
export const fetchGetTkv2OrderPairedCostSettingsAPI = (
  variables: GetTkv2OrderPairedCostSettingsAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    GetTkv2OrderPairedCostSettingsAPIError,
    undefined,
    {},
    {},
    GetTkv2OrderPairedCostSettingsAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/pairedCostSettings',
    method: 'get',
    ...variables,
    signal,
  });

/**
 * Gets the paired cost settings used by this order. If this order does not yet have any settings, the account defaults will be used and stored in the order. (This is idempotent.)
 */
export function getTkv2OrderPairedCostSettingsApiQuery(
  variables: GetTkv2OrderPairedCostSettingsAPIVariables,
): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getTkv2OrderPairedCostSettingsApiQuery(
  variables: GetTkv2OrderPairedCostSettingsAPIVariables,
): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getTkv2OrderPairedCostSettingsApiQuery(
  variables: GetTkv2OrderPairedCostSettingsAPIVariables,
) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v2/orders/{orderID}/pairedCostSettings',
      operationId: 'getTkv2OrderPairedCostSettingsAPI',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) =>
      fetchGetTkv2OrderPairedCostSettingsAPI(variables, signal),
  };
}

/**
 * Gets the paired cost settings used by this order. If this order does not yet have any settings, the account defaults will be used and stored in the order. (This is idempotent.)
 */
export const useSuspenseGetTkv2OrderPairedCostSettingsAPI = <TData = Schemas.Response>(
  variables: GetTkv2OrderPairedCostSettingsAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetTkv2OrderPairedCostSettingsAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<
    Schemas.Response,
    GetTkv2OrderPairedCostSettingsAPIError,
    TData
  >({
    ...getTkv2OrderPairedCostSettingsApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

/**
 * Gets the paired cost settings used by this order. If this order does not yet have any settings, the account defaults will be used and stored in the order. (This is idempotent.)
 */
export const useGetTkv2OrderPairedCostSettingsAPI = <TData = Schemas.Response>(
  variables: GetTkv2OrderPairedCostSettingsAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetTkv2OrderPairedCostSettingsAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, GetTkv2OrderPairedCostSettingsAPIError, TData>({
    ...getTkv2OrderPairedCostSettingsApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type PutTkv2OrderPairedCostSettingsAPIPathParams = {
  orderID: string;
};

export type PutTkv2OrderPairedCostSettingsAPIQueryParams = {
  saveAsDefault?: string;
};

export type PutTkv2OrderPairedCostSettingsAPIError = Fetcher.ErrorWrapper<undefined>;

export type PutTkv2OrderPairedCostSettingsAPIVariables = {
  body?: Schemas.Request;
  pathParams: PutTkv2OrderPairedCostSettingsAPIPathParams;
  queryParams?: PutTkv2OrderPairedCostSettingsAPIQueryParams;
} & ToolkitContext['fetcherOptions'];

/**
 * Update the paired cost settings used by this order. If the saveAsDefault flag is provided, the settings will be saved to the user's account and used in future orders.
 */
export const fetchPutTkv2OrderPairedCostSettingsAPI = (
  variables: PutTkv2OrderPairedCostSettingsAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    PutTkv2OrderPairedCostSettingsAPIError,
    Schemas.Request,
    {},
    PutTkv2OrderPairedCostSettingsAPIQueryParams,
    PutTkv2OrderPairedCostSettingsAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/pairedCostSettings',
    method: 'put',
    ...variables,
    signal,
  });

/**
 * Update the paired cost settings used by this order. If the saveAsDefault flag is provided, the settings will be saved to the user's account and used in future orders.
 */
export const usePutTkv2OrderPairedCostSettingsAPI = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      PutTkv2OrderPairedCostSettingsAPIError,
      PutTkv2OrderPairedCostSettingsAPIVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    Schemas.Response,
    PutTkv2OrderPairedCostSettingsAPIError,
    PutTkv2OrderPairedCostSettingsAPIVariables
  >({
    mutationFn: (variables: PutTkv2OrderPairedCostSettingsAPIVariables) =>
      fetchPutTkv2OrderPairedCostSettingsAPI(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetTkv2OrderDepreciationSettingsAPIPathParams = {
  orderID: string;
};

export type GetTkv2OrderDepreciationSettingsAPIError = Fetcher.ErrorWrapper<undefined>;

export type GetTkv2OrderDepreciationSettingsAPIVariables = {
  pathParams: GetTkv2OrderDepreciationSettingsAPIPathParams;
} & ToolkitContext['fetcherOptions'];

/**
 * Gets the depreciation settings used by this order. If this order does not yet have any settings, the account defaults will be used and stored in the order. (This is idempotent.)
 */
export const fetchGetTkv2OrderDepreciationSettingsAPI = (
  variables: GetTkv2OrderDepreciationSettingsAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    GetTkv2OrderDepreciationSettingsAPIError,
    undefined,
    {},
    {},
    GetTkv2OrderDepreciationSettingsAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/depreciationSettings',
    method: 'get',
    ...variables,
    signal,
  });

/**
 * Gets the depreciation settings used by this order. If this order does not yet have any settings, the account defaults will be used and stored in the order. (This is idempotent.)
 */
export function getTkv2OrderDepreciationSettingsApiQuery(
  variables: GetTkv2OrderDepreciationSettingsAPIVariables,
): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getTkv2OrderDepreciationSettingsApiQuery(
  variables: GetTkv2OrderDepreciationSettingsAPIVariables,
): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getTkv2OrderDepreciationSettingsApiQuery(
  variables: GetTkv2OrderDepreciationSettingsAPIVariables,
) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v2/orders/{orderID}/depreciationSettings',
      operationId: 'getTkv2OrderDepreciationSettingsAPI',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) =>
      fetchGetTkv2OrderDepreciationSettingsAPI(variables, signal),
  };
}

/**
 * Gets the depreciation settings used by this order. If this order does not yet have any settings, the account defaults will be used and stored in the order. (This is idempotent.)
 */
export const useSuspenseGetTkv2OrderDepreciationSettingsAPI = <TData = Schemas.Response>(
  variables: GetTkv2OrderDepreciationSettingsAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetTkv2OrderDepreciationSettingsAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<
    Schemas.Response,
    GetTkv2OrderDepreciationSettingsAPIError,
    TData
  >({
    ...getTkv2OrderDepreciationSettingsApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

/**
 * Gets the depreciation settings used by this order. If this order does not yet have any settings, the account defaults will be used and stored in the order. (This is idempotent.)
 */
export const useGetTkv2OrderDepreciationSettingsAPI = <TData = Schemas.Response>(
  variables: GetTkv2OrderDepreciationSettingsAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetTkv2OrderDepreciationSettingsAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, GetTkv2OrderDepreciationSettingsAPIError, TData>({
    ...getTkv2OrderDepreciationSettingsApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type PutTkv2OrderDepreciationSettingsAPIPathParams = {
  orderID: string;
};

export type PutTkv2OrderDepreciationSettingsAPIQueryParams = {
  saveAsDefault?: string;
};

export type PutTkv2OrderDepreciationSettingsAPIError = Fetcher.ErrorWrapper<undefined>;

export type PutTkv2OrderDepreciationSettingsAPIVariables = {
  body?: Schemas.Request;
  pathParams: PutTkv2OrderDepreciationSettingsAPIPathParams;
  queryParams?: PutTkv2OrderDepreciationSettingsAPIQueryParams;
} & ToolkitContext['fetcherOptions'];

/**
 * Update the depreciation settings used by this order. If the saveAsDefault flag is provided, the settings will be saved to the user's account and used in future orders.
 */
export const fetchPutTkv2OrderDepreciationSettingsAPI = (
  variables: PutTkv2OrderDepreciationSettingsAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    PutTkv2OrderDepreciationSettingsAPIError,
    Schemas.Request,
    {},
    PutTkv2OrderDepreciationSettingsAPIQueryParams,
    PutTkv2OrderDepreciationSettingsAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/depreciationSettings',
    method: 'put',
    ...variables,
    signal,
  });

/**
 * Update the depreciation settings used by this order. If the saveAsDefault flag is provided, the settings will be saved to the user's account and used in future orders.
 */
export const usePutTkv2OrderDepreciationSettingsAPI = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      PutTkv2OrderDepreciationSettingsAPIError,
      PutTkv2OrderDepreciationSettingsAPIVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    Schemas.Response,
    PutTkv2OrderDepreciationSettingsAPIError,
    PutTkv2OrderDepreciationSettingsAPIVariables
  >({
    mutationFn: (variables: PutTkv2OrderDepreciationSettingsAPIVariables) =>
      fetchPutTkv2OrderDepreciationSettingsAPI(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetOrderDepreciationSettingsV2APIPathParams = {
  orderID: string;
};

export type GetOrderDepreciationSettingsV2APIError = Fetcher.ErrorWrapper<undefined>;

export type GetOrderDepreciationSettingsV2APIVariables = {
  pathParams: GetOrderDepreciationSettingsV2APIPathParams;
} & ToolkitContext['fetcherOptions'];

/**
 * Gets the depreciation settings used by this TK 2.0 order.
 */
export const fetchGetOrderDepreciationSettingsV2API = (
  variables: GetOrderDepreciationSettingsV2APIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    GetOrderDepreciationSettingsV2APIError,
    undefined,
    {},
    {},
    GetOrderDepreciationSettingsV2APIPathParams
  >({
    url: '/api/v2/orders/{orderID}/depreciationSettingsV2',
    method: 'get',
    ...variables,
    signal,
  });

/**
 * Gets the depreciation settings used by this TK 2.0 order.
 */
export function getOrderDepreciationSettingsV2ApiQuery(
  variables: GetOrderDepreciationSettingsV2APIVariables,
): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getOrderDepreciationSettingsV2ApiQuery(
  variables: GetOrderDepreciationSettingsV2APIVariables,
): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getOrderDepreciationSettingsV2ApiQuery(
  variables: GetOrderDepreciationSettingsV2APIVariables,
) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v2/orders/{orderID}/depreciationSettingsV2',
      operationId: 'getOrderDepreciationSettingsV2API',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) =>
      fetchGetOrderDepreciationSettingsV2API(variables, signal),
  };
}

/**
 * Gets the depreciation settings used by this TK 2.0 order.
 */
export const useSuspenseGetOrderDepreciationSettingsV2API = <TData = Schemas.Response>(
  variables: GetOrderDepreciationSettingsV2APIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetOrderDepreciationSettingsV2APIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<
    Schemas.Response,
    GetOrderDepreciationSettingsV2APIError,
    TData
  >({
    ...getOrderDepreciationSettingsV2ApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

/**
 * Gets the depreciation settings used by this TK 2.0 order.
 */
export const useGetOrderDepreciationSettingsV2API = <TData = Schemas.Response>(
  variables: GetOrderDepreciationSettingsV2APIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetOrderDepreciationSettingsV2APIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, GetOrderDepreciationSettingsV2APIError, TData>({
    ...getOrderDepreciationSettingsV2ApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type PutOrderDepreciationSettingsV2APIPathParams = {
  orderID: string;
};

export type PutOrderDepreciationSettingsV2APIError = Fetcher.ErrorWrapper<undefined>;

export type PutOrderDepreciationSettingsV2APIVariables = {
  body?: Schemas.Request;
  pathParams: PutOrderDepreciationSettingsV2APIPathParams;
} & ToolkitContext['fetcherOptions'];

/**
 * Update the depreciation settings used by this TK 2.0 order.
 */
export const fetchPutOrderDepreciationSettingsV2API = (
  variables: PutOrderDepreciationSettingsV2APIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    PutOrderDepreciationSettingsV2APIError,
    Schemas.Request,
    {},
    {},
    PutOrderDepreciationSettingsV2APIPathParams
  >({
    url: '/api/v2/orders/{orderID}/depreciationSettingsV2',
    method: 'put',
    ...variables,
    signal,
  });

/**
 * Update the depreciation settings used by this TK 2.0 order.
 */
export const usePutOrderDepreciationSettingsV2API = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      PutOrderDepreciationSettingsV2APIError,
      PutOrderDepreciationSettingsV2APIVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    Schemas.Response,
    PutOrderDepreciationSettingsV2APIError,
    PutOrderDepreciationSettingsV2APIVariables
  >({
    mutationFn: (variables: PutOrderDepreciationSettingsV2APIVariables) =>
      fetchPutOrderDepreciationSettingsV2API(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type ListPairedSalesStudiesQueryParams = {
  orderID?: string;
};

export type ListPairedSalesStudiesError = Fetcher.ErrorWrapper<undefined>;

export type ListPairedSalesStudiesVariables = {
  queryParams?: ListPairedSalesStudiesQueryParams;
} & ToolkitContext['fetcherOptions'];

/**
 * Gets a list of all Paired Sales `Study` entities.
 */
export const fetchListPairedSalesStudies = (
  variables: ListPairedSalesStudiesVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    ListPairedSalesStudiesError,
    undefined,
    {},
    ListPairedSalesStudiesQueryParams,
    {}
  >({ url: '/api/v2/pairedSales', method: 'get', ...variables, signal });

/**
 * Gets a list of all Paired Sales `Study` entities.
 */
export function listPairedSalesStudiesQuery(variables: ListPairedSalesStudiesVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function listPairedSalesStudiesQuery(variables: ListPairedSalesStudiesVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function listPairedSalesStudiesQuery(variables: ListPairedSalesStudiesVariables) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v2/pairedSales',
      operationId: 'listPairedSalesStudies',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) => fetchListPairedSalesStudies(variables, signal),
  };
}

/**
 * Gets a list of all Paired Sales `Study` entities.
 */
export const useSuspenseListPairedSalesStudies = <TData = Schemas.Response>(
  variables: ListPairedSalesStudiesVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, ListPairedSalesStudiesError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<Schemas.Response, ListPairedSalesStudiesError, TData>({
    ...listPairedSalesStudiesQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

/**
 * Gets a list of all Paired Sales `Study` entities.
 */
export const useListPairedSalesStudies = <TData = Schemas.Response>(
  variables: ListPairedSalesStudiesVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, ListPairedSalesStudiesError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, ListPairedSalesStudiesError, TData>({
    ...listPairedSalesStudiesQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type CreatePairedSalesStudyQueryParams = {
  orderID?: string;
};

export type CreatePairedSalesStudyError = Fetcher.ErrorWrapper<undefined>;

export type CreatePairedSalesStudyVariables = {
  body?: Schemas.Request;
  queryParams?: CreatePairedSalesStudyQueryParams;
} & ToolkitContext['fetcherOptions'];

/**
 * Creates a new instance of a paired sales `Study`
 */
export const fetchCreatePairedSalesStudy = (
  variables: CreatePairedSalesStudyVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    CreatePairedSalesStudyError,
    Schemas.Request,
    {},
    CreatePairedSalesStudyQueryParams,
    {}
  >({ url: '/api/v2/pairedSales', method: 'post', ...variables, signal });

/**
 * Creates a new instance of a paired sales `Study`
 */
export const useCreatePairedSalesStudy = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      CreatePairedSalesStudyError,
      CreatePairedSalesStudyVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    Schemas.Response,
    CreatePairedSalesStudyError,
    CreatePairedSalesStudyVariables
  >({
    mutationFn: (variables: CreatePairedSalesStudyVariables) =>
      fetchCreatePairedSalesStudy(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetPairedSalesStudyPathParams = {
  studyID: string;
};

export type GetPairedSalesStudyQueryParams = {
  orderID?: string;
};

export type GetPairedSalesStudyError = Fetcher.ErrorWrapper<undefined>;

export type GetPairedSalesStudyVariables = {
  pathParams: GetPairedSalesStudyPathParams;
  queryParams?: GetPairedSalesStudyQueryParams;
} & ToolkitContext['fetcherOptions'];

/**
 * Gets the details of a single instance of a Paired Sales `Study`
 */
export const fetchGetPairedSalesStudy = (
  variables: GetPairedSalesStudyVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    GetPairedSalesStudyError,
    undefined,
    {},
    GetPairedSalesStudyQueryParams,
    GetPairedSalesStudyPathParams
  >({
    url: '/api/v2/pairedSales/{studyID}',
    method: 'get',
    ...variables,
    signal,
  });

/**
 * Gets the details of a single instance of a Paired Sales `Study`
 */
export function getPairedSalesStudyQuery(variables: GetPairedSalesStudyVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getPairedSalesStudyQuery(variables: GetPairedSalesStudyVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getPairedSalesStudyQuery(variables: GetPairedSalesStudyVariables) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v2/pairedSales/{studyID}',
      operationId: 'getPairedSalesStudy',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) => fetchGetPairedSalesStudy(variables, signal),
  };
}

/**
 * Gets the details of a single instance of a Paired Sales `Study`
 */
export const useSuspenseGetPairedSalesStudy = <TData = Schemas.Response>(
  variables: GetPairedSalesStudyVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetPairedSalesStudyError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<Schemas.Response, GetPairedSalesStudyError, TData>({
    ...getPairedSalesStudyQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

/**
 * Gets the details of a single instance of a Paired Sales `Study`
 */
export const useGetPairedSalesStudy = <TData = Schemas.Response>(
  variables: GetPairedSalesStudyVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetPairedSalesStudyError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, GetPairedSalesStudyError, TData>({
    ...getPairedSalesStudyQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type UpdatePairedSalesStudyPathParams = {
  studyID: string;
};

export type UpdatePairedSalesStudyQueryParams = {
  orderID?: string;
};

export type UpdatePairedSalesStudyError = Fetcher.ErrorWrapper<undefined>;

export type UpdatePairedSalesStudyVariables = {
  body?: Schemas.Request;
  pathParams: UpdatePairedSalesStudyPathParams;
  queryParams?: UpdatePairedSalesStudyQueryParams;
} & ToolkitContext['fetcherOptions'];

/**
 * Updates the details of a single instance of a Paired Sales `Study`
 */
export const fetchUpdatePairedSalesStudy = (
  variables: UpdatePairedSalesStudyVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    UpdatePairedSalesStudyError,
    Schemas.Request,
    {},
    UpdatePairedSalesStudyQueryParams,
    UpdatePairedSalesStudyPathParams
  >({
    url: '/api/v2/pairedSales/{studyID}',
    method: 'put',
    ...variables,
    signal,
  });

/**
 * Updates the details of a single instance of a Paired Sales `Study`
 */
export const useUpdatePairedSalesStudy = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      UpdatePairedSalesStudyError,
      UpdatePairedSalesStudyVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    Schemas.Response,
    UpdatePairedSalesStudyError,
    UpdatePairedSalesStudyVariables
  >({
    mutationFn: (variables: UpdatePairedSalesStudyVariables) =>
      fetchUpdatePairedSalesStudy(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetPropertyImageAnalysisPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
  /**
   * Either compID or the string "subject" for the subject.
   */
  propertyID: string;
};

export type GetPropertyImageAnalysisError = Fetcher.ErrorWrapper<undefined>;

export type GetPropertyImageAnalysisVariables = {
  pathParams: GetPropertyImageAnalysisPathParams;
} & ToolkitContext['fetcherOptions'];

/**
 * Runs LLM image analysis on a property and stores results.
 */
export const fetchGetPropertyImageAnalysis = (
  variables: GetPropertyImageAnalysisVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    GetPropertyImageAnalysisError,
    undefined,
    {},
    {},
    GetPropertyImageAnalysisPathParams
  >({
    url: '/api/v2/orders/{orderID}/property/{propertyID}/imageAnalysis',
    method: 'get',
    ...variables,
    signal,
  });

/**
 * Runs LLM image analysis on a property and stores results.
 */
export function getPropertyImageAnalysisQuery(variables: GetPropertyImageAnalysisVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getPropertyImageAnalysisQuery(variables: GetPropertyImageAnalysisVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getPropertyImageAnalysisQuery(variables: GetPropertyImageAnalysisVariables) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v2/orders/{orderID}/property/{propertyID}/imageAnalysis',
      operationId: 'getPropertyImageAnalysis',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) => fetchGetPropertyImageAnalysis(variables, signal),
  };
}

/**
 * Runs LLM image analysis on a property and stores results.
 */
export const useSuspenseGetPropertyImageAnalysis = <TData = Schemas.Response>(
  variables: GetPropertyImageAnalysisVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetPropertyImageAnalysisError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<Schemas.Response, GetPropertyImageAnalysisError, TData>({
    ...getPropertyImageAnalysisQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

/**
 * Runs LLM image analysis on a property and stores results.
 */
export const useGetPropertyImageAnalysis = <TData = Schemas.Response>(
  variables: GetPropertyImageAnalysisVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetPropertyImageAnalysisError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, GetPropertyImageAnalysisError, TData>({
    ...getPropertyImageAnalysisQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type GetCompImagesPathParams = {
  /**
   * A unique identifier for a property listing.
   */
  id: string;
};

export type GetCompImagesError = Fetcher.ErrorWrapper<undefined>;

export type GetCompImagesVariables = {
  pathParams: GetCompImagesPathParams;
} & ToolkitContext['fetcherOptions'];

/**
 * Gets the comp images if available
 */
export const fetchGetCompImages = (variables: GetCompImagesVariables, signal?: AbortSignal) =>
  toolkitFetch<Schemas.Response, GetCompImagesError, undefined, {}, {}, GetCompImagesPathParams>({
    url: '/api/v2/compImages/{id}',
    method: 'get',
    ...variables,
    signal,
  });

/**
 * Gets the comp images if available
 */
export function getCompImagesQuery(variables: GetCompImagesVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getCompImagesQuery(variables: GetCompImagesVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getCompImagesQuery(variables: GetCompImagesVariables) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v2/compImages/{id}',
      operationId: 'getCompImages',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) => fetchGetCompImages(variables, signal),
  };
}

/**
 * Gets the comp images if available
 */
export const useSuspenseGetCompImages = <TData = Schemas.Response>(
  variables: GetCompImagesVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetCompImagesError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<Schemas.Response, GetCompImagesError, TData>({
    ...getCompImagesQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

/**
 * Gets the comp images if available
 */
export const useGetCompImages = <TData = Schemas.Response>(
  variables: GetCompImagesVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetCompImagesError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, GetCompImagesError, TData>({
    ...getCompImagesQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type GetCompRecommendationPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type GetCompRecommendationError = Fetcher.ErrorWrapper<{
  status: 404;
  payload: Schemas.ErrorResponse;
}>;

export type GetCompRecommendationVariables = {
  pathParams: GetCompRecommendationPathParams;
} & ToolkitContext['fetcherOptions'];

/**
 * Gets the comp recommendation attachment
 */
export const fetchGetCompRecommendation = (
  variables: GetCompRecommendationVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    GetCompRecommendationError,
    undefined,
    {},
    {},
    GetCompRecommendationPathParams
  >({
    url: '/api/v2/orders/{orderID}/compRecommendation',
    method: 'get',
    ...variables,
    signal,
  });

/**
 * Gets the comp recommendation attachment
 */
export function getCompRecommendationQuery(variables: GetCompRecommendationVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getCompRecommendationQuery(variables: GetCompRecommendationVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getCompRecommendationQuery(variables: GetCompRecommendationVariables) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v2/orders/{orderID}/compRecommendation',
      operationId: 'getCompRecommendation',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) => fetchGetCompRecommendation(variables, signal),
  };
}

/**
 * Gets the comp recommendation attachment
 */
export const useSuspenseGetCompRecommendation = <TData = Schemas.Response>(
  variables: GetCompRecommendationVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetCompRecommendationError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<Schemas.Response, GetCompRecommendationError, TData>({
    ...getCompRecommendationQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

/**
 * Gets the comp recommendation attachment
 */
export const useGetCompRecommendation = <TData = Schemas.Response>(
  variables: GetCompRecommendationVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetCompRecommendationError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, GetCompRecommendationError, TData>({
    ...getCompRecommendationQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type PostCompRecommendationPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type PostCompRecommendationError = Fetcher.ErrorWrapper<undefined>;

export type PostCompRecommendationVariables = {
  pathParams: PostCompRecommendationPathParams;
  body?: Schemas.Request;
} & ToolkitContext['fetcherOptions'];

/**
 * Runs comp recommendations based on subject property. Retrieves stored order attachment.
 */
export const fetchPostCompRecommendation = (
  variables: PostCompRecommendationVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    PostCompRecommendationError,
    Schemas.Request,
    {},
    {},
    PostCompRecommendationPathParams
  >({
    url: '/api/v2/orders/{orderID}/compRecommendation',
    method: 'post',
    ...variables,
    signal,
  });

/**
 * Runs comp recommendations based on subject property. Retrieves stored order attachment.
 */
export const usePostCompRecommendation = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      PostCompRecommendationError,
      PostCompRecommendationVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    Schemas.Response,
    PostCompRecommendationError,
    PostCompRecommendationVariables
  >({
    mutationFn: (variables: PostCompRecommendationVariables) =>
      fetchPostCompRecommendation(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetRentalCompRecommendationPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type GetRentalCompRecommendationError = Fetcher.ErrorWrapper<{
  status: 404;
  payload: Schemas.ErrorResponse;
}>;

export type GetRentalCompRecommendationVariables = {
  pathParams: GetRentalCompRecommendationPathParams;
} & ToolkitContext['fetcherOptions'];

/**
 * Gets the rental comp recommendation attachment
 */
export const fetchGetRentalCompRecommendation = (
  variables: GetRentalCompRecommendationVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    GetRentalCompRecommendationError,
    undefined,
    {},
    {},
    GetRentalCompRecommendationPathParams
  >({
    url: '/api/v2/orders/{orderID}/rentalCompRecommendation',
    method: 'get',
    ...variables,
    signal,
  });

/**
 * Gets the rental comp recommendation attachment
 */
export function getRentalCompRecommendationQuery(variables: GetRentalCompRecommendationVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getRentalCompRecommendationQuery(variables: GetRentalCompRecommendationVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getRentalCompRecommendationQuery(variables: GetRentalCompRecommendationVariables) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v2/orders/{orderID}/rentalCompRecommendation',
      operationId: 'getRentalCompRecommendation',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) => fetchGetRentalCompRecommendation(variables, signal),
  };
}

/**
 * Gets the rental comp recommendation attachment
 */
export const useSuspenseGetRentalCompRecommendation = <TData = Schemas.Response>(
  variables: GetRentalCompRecommendationVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetRentalCompRecommendationError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<Schemas.Response, GetRentalCompRecommendationError, TData>({
    ...getRentalCompRecommendationQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

/**
 * Gets the rental comp recommendation attachment
 */
export const useGetRentalCompRecommendation = <TData = Schemas.Response>(
  variables: GetRentalCompRecommendationVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetRentalCompRecommendationError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, GetRentalCompRecommendationError, TData>({
    ...getRentalCompRecommendationQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type PostRentalCompRecommendationPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type PostRentalCompRecommendationError = Fetcher.ErrorWrapper<undefined>;

export type PostRentalCompRecommendationVariables = {
  pathParams: PostRentalCompRecommendationPathParams;
} & ToolkitContext['fetcherOptions'];

/**
 * Runs rental comp recommendations based on subject property. Retrieves stored order attachment.
 */
export const fetchPostRentalCompRecommendation = (
  variables: PostRentalCompRecommendationVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    PostRentalCompRecommendationError,
    undefined,
    {},
    {},
    PostRentalCompRecommendationPathParams
  >({
    url: '/api/v2/orders/{orderID}/rentalCompRecommendation',
    method: 'post',
    ...variables,
    signal,
  });

/**
 * Runs rental comp recommendations based on subject property. Retrieves stored order attachment.
 */
export const usePostRentalCompRecommendation = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      PostRentalCompRecommendationError,
      PostRentalCompRecommendationVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    Schemas.Response,
    PostRentalCompRecommendationError,
    PostRentalCompRecommendationVariables
  >({
    mutationFn: (variables: PostRentalCompRecommendationVariables) =>
      fetchPostRentalCompRecommendation(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetListingByAddressError = Fetcher.ErrorWrapper<undefined>;

export type GetListingByAddressVariables = {
  body?: Schemas.Request;
} & ToolkitContext['fetcherOptions'];

/**
 * Gets a listing by address
 */
export const fetchGetListingByAddress = (
  variables: GetListingByAddressVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<Schemas.Response, GetListingByAddressError, Schemas.Request, {}, {}, {}>({
    url: '/api/v2/listingByAddress',
    method: 'post',
    ...variables,
    signal,
  });

/**
 * Gets a listing by address
 */
export const useGetListingByAddress = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      GetListingByAddressError,
      GetListingByAddressVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    Schemas.Response,
    GetListingByAddressError,
    GetListingByAddressVariables
  >({
    mutationFn: (variables: GetListingByAddressVariables) =>
      fetchGetListingByAddress(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type PostImageAnalysisError = Fetcher.ErrorWrapper<undefined>;

export type PostImageAnalysisVariables = {
  body?: Schemas.Request;
} & ToolkitContext['fetcherOptions'];

/**
 * Run an image analysis
 */
export const fetchPostImageAnalysis = (
  variables: PostImageAnalysisVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<Schemas.Response, PostImageAnalysisError, Schemas.Request, {}, {}, {}>({
    url: '/api/v2/imageAnalysis',
    method: 'post',
    ...variables,
    signal,
  });

/**
 * Run an image analysis
 */
export const usePostImageAnalysis = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      PostImageAnalysisError,
      PostImageAnalysisVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    Schemas.Response,
    PostImageAnalysisError,
    PostImageAnalysisVariables
  >({
    mutationFn: (variables: PostImageAnalysisVariables) =>
      fetchPostImageAnalysis(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetStripeSubscriptionStatusError = Fetcher.ErrorWrapper<undefined>;

export type GetStripeSubscriptionStatusVariables = ToolkitContext['fetcherOptions'];

/**
 * Gets the subscription status
 */
export const fetchGetStripeSubscriptionStatus = (
  variables: GetStripeSubscriptionStatusVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<Schemas.Response, GetStripeSubscriptionStatusError, undefined, {}, {}, {}>({
    url: '/api/v2/stripe/subscriptionStatus',
    method: 'get',
    ...variables,
    signal,
  });

/**
 * Gets the subscription status
 */
export function getStripeSubscriptionStatusQuery(variables: GetStripeSubscriptionStatusVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getStripeSubscriptionStatusQuery(variables: GetStripeSubscriptionStatusVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getStripeSubscriptionStatusQuery(variables: GetStripeSubscriptionStatusVariables) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v2/stripe/subscriptionStatus',
      operationId: 'getStripeSubscriptionStatus',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) => fetchGetStripeSubscriptionStatus(variables, signal),
  };
}

/**
 * Gets the subscription status
 */
export const useSuspenseGetStripeSubscriptionStatus = <TData = Schemas.Response>(
  variables: GetStripeSubscriptionStatusVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetStripeSubscriptionStatusError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<Schemas.Response, GetStripeSubscriptionStatusError, TData>({
    ...getStripeSubscriptionStatusQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

/**
 * Gets the subscription status
 */
export const useGetStripeSubscriptionStatus = <TData = Schemas.Response>(
  variables: GetStripeSubscriptionStatusVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetStripeSubscriptionStatusError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, GetStripeSubscriptionStatusError, TData>({
    ...getStripeSubscriptionStatusQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type CreateStripeCustomerSessionError = Fetcher.ErrorWrapper<undefined>;

export type CreateStripeCustomerSessionVariables = ToolkitContext['fetcherOptions'];

/**
 * Creates a customer session
 */
export const fetchCreateStripeCustomerSession = (
  variables: CreateStripeCustomerSessionVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<Schemas.Response, CreateStripeCustomerSessionError, undefined, {}, {}, {}>({
    url: '/api/v2/stripe/customerSession',
    method: 'post',
    ...variables,
    signal,
  });

/**
 * Creates a customer session
 */
export const useCreateStripeCustomerSession = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      CreateStripeCustomerSessionError,
      CreateStripeCustomerSessionVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    Schemas.Response,
    CreateStripeCustomerSessionError,
    CreateStripeCustomerSessionVariables
  >({
    mutationFn: (variables: CreateStripeCustomerSessionVariables) =>
      fetchCreateStripeCustomerSession(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type CreateStripeSubscriptionError = Fetcher.ErrorWrapper<undefined>;

export type CreateStripeSubscriptionVariables = {
  body?: Schemas.Request;
} & ToolkitContext['fetcherOptions'];

/**
 * Creates a Stripe subscription for the authenticated user's Stripe customer ID.
 */
export const fetchCreateStripeSubscription = (
  variables: CreateStripeSubscriptionVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<Schemas.Response, CreateStripeSubscriptionError, Schemas.Request, {}, {}, {}>({
    url: '/api/v2/stripe/subscription',
    method: 'post',
    ...variables,
    signal,
  });

/**
 * Creates a Stripe subscription for the authenticated user's Stripe customer ID.
 */
export const useCreateStripeSubscription = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      CreateStripeSubscriptionError,
      CreateStripeSubscriptionVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    Schemas.Response,
    CreateStripeSubscriptionError,
    CreateStripeSubscriptionVariables
  >({
    mutationFn: (variables: CreateStripeSubscriptionVariables) =>
      fetchCreateStripeSubscription(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type UpdateStripeBillingAddressError = Fetcher.ErrorWrapper<undefined>;

export type UpdateStripeBillingAddressVariables = {
  body?: Schemas.Request;
} & ToolkitContext['fetcherOptions'];

/**
 * Updates the billing address of the authenticated user's Stripe customer ID.
 */
export const fetchUpdateStripeBillingAddress = (
  variables: UpdateStripeBillingAddressVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<Schemas.Response, UpdateStripeBillingAddressError, Schemas.Request, {}, {}, {}>({
    url: '/api/v2/stripe/billingAddress',
    method: 'patch',
    ...variables,
    signal,
  });

/**
 * Updates the billing address of the authenticated user's Stripe customer ID.
 */
export const useUpdateStripeBillingAddress = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      UpdateStripeBillingAddressError,
      UpdateStripeBillingAddressVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    Schemas.Response,
    UpdateStripeBillingAddressError,
    UpdateStripeBillingAddressVariables
  >({
    mutationFn: (variables: UpdateStripeBillingAddressVariables) =>
      fetchUpdateStripeBillingAddress(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type CalculateStripePricingBreakdownError = Fetcher.ErrorWrapper<undefined>;

export type CalculateStripePricingBreakdownVariables = {
  body?: Schemas.Request;
} & ToolkitContext['fetcherOptions'];

/**
 * Calculates the pricing breakdown with sales tax and promo code for a given subscription price and address.
 */
export const fetchCalculateStripePricingBreakdown = (
  variables: CalculateStripePricingBreakdownVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<Schemas.Response, CalculateStripePricingBreakdownError, Schemas.Request, {}, {}, {}>(
    {
      url: '/api/v2/stripe/pricingBreakdown',
      method: 'post',
      ...variables,
      signal,
    },
  );

/**
 * Calculates the pricing breakdown with sales tax and promo code for a given subscription price and address.
 */
export const useCalculateStripePricingBreakdown = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      CalculateStripePricingBreakdownError,
      CalculateStripePricingBreakdownVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    Schemas.Response,
    CalculateStripePricingBreakdownError,
    CalculateStripePricingBreakdownVariables
  >({
    mutationFn: (variables: CalculateStripePricingBreakdownVariables) =>
      fetchCalculateStripePricingBreakdown(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type ValidatePromotionCodeError = Fetcher.ErrorWrapper<undefined>;

export type ValidatePromotionCodeVariables = {
  body?: Schemas.Request;
} & ToolkitContext['fetcherOptions'];

export const fetchValidatePromotionCode = (
  variables: ValidatePromotionCodeVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<Schemas.Response, ValidatePromotionCodeError, Schemas.Request, {}, {}, {}>({
    url: '/api/v2/stripe/validatePromotionCode',
    method: 'post',
    ...variables,
    signal,
  });

export const useValidatePromotionCode = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      ValidatePromotionCodeError,
      ValidatePromotionCodeVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    Schemas.Response,
    ValidatePromotionCodeError,
    ValidatePromotionCodeVariables
  >({
    mutationFn: (variables: ValidatePromotionCodeVariables) =>
      fetchValidatePromotionCode(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type ConvertTrialError = Fetcher.ErrorWrapper<undefined>;

export type ConvertTrialVariables = {
  body?: Schemas.Request;
} & ToolkitContext['fetcherOptions'];

export const fetchConvertTrial = (variables: ConvertTrialVariables, signal?: AbortSignal) =>
  toolkitFetch<Schemas.Response, ConvertTrialError, Schemas.Request, {}, {}, {}>({
    url: '/api/v2/stripe/convertTrial',
    method: 'post',
    ...variables,
    signal,
  });

export const useConvertTrial = (
  options?: Omit<
    reactQuery.UseMutationOptions<Schemas.Response, ConvertTrialError, ConvertTrialVariables>,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<Schemas.Response, ConvertTrialError, ConvertTrialVariables>({
    mutationFn: (variables: ConvertTrialVariables) =>
      fetchConvertTrial(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type AutofillPropertyDataError = Fetcher.ErrorWrapper<undefined>;

export type AutofillPropertyDataVariables = {
  body?: Schemas.Request;
} & ToolkitContext['fetcherOptions'];

/**
 * Retrieves all available property data
 */
export const fetchAutofillPropertyData = (
  variables: AutofillPropertyDataVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<Schemas.Response, AutofillPropertyDataError, Schemas.Request, {}, {}, {}>({
    url: '/api/v2/autofill/property',
    method: 'post',
    ...variables,
    signal,
  });

/**
 * Retrieves all available property data
 */
export const useAutofillPropertyData = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      AutofillPropertyDataError,
      AutofillPropertyDataVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<
    Schemas.Response,
    AutofillPropertyDataError,
    AutofillPropertyDataVariables
  >({
    mutationFn: (variables: AutofillPropertyDataVariables) =>
      fetchAutofillPropertyData(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type OrchestrateAutofillPropertyAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
  /**
   * Either compId or "subjectData".
   */
  propertyId: string;
};

export type OrchestrateAutofillPropertyAPIQueryParams = {
  /**
   * A list of sources to use for autofill.
   */
  sources?: string[];
  /**
   * Whether to include backup listing data.
   */
  includeBackupListingData?: boolean;
  /**
   * The type of listing to fetch.
   */
  listingType?: 'listing' | 'rentalListing';
};

export type OrchestrateAutofillPropertyAPIError = Fetcher.ErrorWrapper<undefined>;

export type OrchestrateAutofillPropertyAPIVariables = {
  pathParams: OrchestrateAutofillPropertyAPIPathParams;
  queryParams?: OrchestrateAutofillPropertyAPIQueryParams;
} & ToolkitContext['fetcherOptions'];

/**
 * Orchestrate autofill
 */
export const fetchOrchestrateAutofillPropertyAPI = (
  variables: OrchestrateAutofillPropertyAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    OrchestrateAutofillPropertyAPIError,
    undefined,
    {},
    OrchestrateAutofillPropertyAPIQueryParams,
    OrchestrateAutofillPropertyAPIPathParams
  >({
    url: '/api/v2/autofill/orchestrate/{orderID}/{propertyId}',
    method: 'get',
    ...variables,
    signal,
  });

/**
 * Orchestrate autofill
 */
export function orchestrateAutofillPropertyApiQuery(
  variables: OrchestrateAutofillPropertyAPIVariables,
): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function orchestrateAutofillPropertyApiQuery(
  variables: OrchestrateAutofillPropertyAPIVariables,
): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function orchestrateAutofillPropertyApiQuery(
  variables: OrchestrateAutofillPropertyAPIVariables,
) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v2/autofill/orchestrate/{orderID}/{propertyId}',
      operationId: 'orchestrateAutofillPropertyAPI',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) => fetchOrchestrateAutofillPropertyAPI(variables, signal),
  };
}

/**
 * Orchestrate autofill
 */
export const useSuspenseOrchestrateAutofillPropertyAPI = <TData = Schemas.Response>(
  variables: OrchestrateAutofillPropertyAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, OrchestrateAutofillPropertyAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<Schemas.Response, OrchestrateAutofillPropertyAPIError, TData>({
    ...orchestrateAutofillPropertyApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

/**
 * Orchestrate autofill
 */
export const useOrchestrateAutofillPropertyAPI = <TData = Schemas.Response>(
  variables: OrchestrateAutofillPropertyAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, OrchestrateAutofillPropertyAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, OrchestrateAutofillPropertyAPIError, TData>({
    ...orchestrateAutofillPropertyApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type GetCoordinatesForAddressesAPIQueryParams = {
  /**
   * A list of addresses to geocode.
   */
  addresses: string[];
};

export type GetCoordinatesForAddressesAPIError = Fetcher.ErrorWrapper<undefined>;

export type GetCoordinatesForAddressesAPIVariables = {
  queryParams: GetCoordinatesForAddressesAPIQueryParams;
  /**
   * Headers to inject in the fetcher
   */
  headers?: {
    authorization?: string;
    [REQUEST_SESSION_ID_HEADER]?: string;
  };
  /**
   * Function to get the access token for the request
   */
  accessTokenFn?: () => Promise<string>;
};

/**
 * Get coordinates for a list of addresses
 */
export const fetchGetCoordinatesForAddressesAPI = (
  variables: GetCoordinatesForAddressesAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    GetCoordinatesForAddressesAPIError,
    undefined,
    {},
    GetCoordinatesForAddressesAPIQueryParams,
    {}
    // @ts-expect-error - CodeGen type issue
  >({
    url: '/api/v2/geocoding/coordinates',
    method: 'get',
    ...variables,
    signal,
  });

/**
 * Get coordinates for a list of addresses
 */
export function getCoordinatesForAddressesApiQuery(
  variables: GetCoordinatesForAddressesAPIVariables,
): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getCoordinatesForAddressesApiQuery(
  variables: GetCoordinatesForAddressesAPIVariables,
): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getCoordinatesForAddressesApiQuery(
  variables: GetCoordinatesForAddressesAPIVariables,
) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v2/geocoding/coordinates',
      operationId: 'getCoordinatesForAddressesAPI',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) => fetchGetCoordinatesForAddressesAPI(variables, signal),
  };
}

/**
 * Get coordinates for a list of addresses
 */
export const useSuspenseGetCoordinatesForAddressesAPI = <TData = Schemas.Response>(
  variables: GetCoordinatesForAddressesAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetCoordinatesForAddressesAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<Schemas.Response, GetCoordinatesForAddressesAPIError, TData>({
    ...getCoordinatesForAddressesApiQuery({ ...fetcherOptions, ...variables }),
    ...options,
    ...queryOptions,
  });
};

/**
 * Get coordinates for a list of addresses
 */
export const useGetCoordinatesForAddressesAPI = <TData = Schemas.Response>(
  variables: GetCoordinatesForAddressesAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetCoordinatesForAddressesAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, GetCoordinatesForAddressesAPIError, TData>({
    ...getCoordinatesForAddressesApiQuery({ ...fetcherOptions, ...variables }),
    ...options,
    ...queryOptions,
  });
};

export type GetMarketAggregateDataAPIPathParams = {
  /**
   * A unique identifier for a `Order`.
   */
  orderID: string;
};

export type GetMarketAggregateDataAPIQueryParams = {
  /**
   * The geographic type to aggregate by (Zip, county, city).
   */
  geoType: string;
  /**
   * The geographic identifier to aggregate by (e.g. 90210 for Zip).
   */
  geoId: string;
};

export type GetMarketAggregateDataAPIError = Fetcher.ErrorWrapper<undefined>;

export type GetMarketAggregateDataAPIVariables = {
  pathParams: GetMarketAggregateDataAPIPathParams;
  queryParams: GetMarketAggregateDataAPIQueryParams;
} & ToolkitContext['fetcherOptions'];

/**
 * Get the market aggregate for an order
 */
export const fetchGetMarketAggregateDataAPI = (
  variables: GetMarketAggregateDataAPIVariables,
  signal?: AbortSignal,
) =>
  toolkitFetch<
    Schemas.Response,
    GetMarketAggregateDataAPIError,
    undefined,
    {},
    GetMarketAggregateDataAPIQueryParams,
    GetMarketAggregateDataAPIPathParams
  >({
    url: '/api/v2/orders/{orderID}/marketConditions/marketAggregateData',
    method: 'get',
    ...variables,
    signal,
  });

/**
 * Get the market aggregate for an order
 */
export function getMarketAggregateDataApiQuery(variables: GetMarketAggregateDataAPIVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getMarketAggregateDataApiQuery(variables: GetMarketAggregateDataAPIVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function getMarketAggregateDataApiQuery(variables: GetMarketAggregateDataAPIVariables) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v2/orders/{orderID}/marketConditions/marketAggregateData',
      operationId: 'getMarketAggregateDataAPI',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) => fetchGetMarketAggregateDataAPI(variables, signal),
  };
}

/**
 * Get the market aggregate for an order
 */
export const useSuspenseGetMarketAggregateDataAPI = <TData = Schemas.Response>(
  variables: GetMarketAggregateDataAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetMarketAggregateDataAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<Schemas.Response, GetMarketAggregateDataAPIError, TData>({
    ...getMarketAggregateDataApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

/**
 * Get the market aggregate for an order
 */
export const useGetMarketAggregateDataAPI = <TData = Schemas.Response>(
  variables: GetMarketAggregateDataAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, GetMarketAggregateDataAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, GetMarketAggregateDataAPIError, TData>({
    ...getMarketAggregateDataApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type ListAccountsAPIQueryParams = {
  /**
   * Filter accounts by email address.
   */
  email?: string;
  /**
   * Filter accounts by name (first or last).
   */
  name?: string;
  /**
   * The maximum number of accounts to return.
   *
   * @default 100
   */
  limit?: string;
  /**
   * The number of accounts to skip before starting to collect the result set.
   *
   * @default 0
   */
  offset?: string;
};

export type ListAccountsAPIError = Fetcher.ErrorWrapper<undefined>;

export type ListAccountsAPIVariables = {
  queryParams?: ListAccountsAPIQueryParams;
} & ToolkitContext['fetcherOptions'];

/**
 * List user accounts
 */
export const fetchListAccountsAPI = (variables: ListAccountsAPIVariables, signal?: AbortSignal) =>
  toolkitFetch<
    Schemas.Response,
    ListAccountsAPIError,
    undefined,
    {},
    ListAccountsAPIQueryParams,
    {}
  >({ url: '/api/v2/admin/accounts', method: 'get', ...variables, signal });

/**
 * List user accounts
 */
export function listAccountsApiQuery(variables: ListAccountsAPIVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function listAccountsApiQuery(variables: ListAccountsAPIVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Schemas.Response>;
};

export function listAccountsApiQuery(variables: ListAccountsAPIVariables) {
  return {
    queryKey: queryKeyFn({
      path: '/api/v2/admin/accounts',
      operationId: 'listAccountsAPI',
      variables,
    }),
    queryFn: ({ signal }: QueryFnOptions) => fetchListAccountsAPI(variables, signal),
  };
}

/**
 * List user accounts
 */
export const useSuspenseListAccountsAPI = <TData = Schemas.Response>(
  variables: ListAccountsAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, ListAccountsAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useSuspenseQuery<Schemas.Response, ListAccountsAPIError, TData>({
    ...listAccountsApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

/**
 * List user accounts
 */
export const useListAccountsAPI = <TData = Schemas.Response>(
  variables: ListAccountsAPIVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Response, ListAccountsAPIError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { queryOptions, fetcherOptions } = useToolkitContext(options);
  return reactQuery.useQuery<Schemas.Response, ListAccountsAPIError, TData>({
    ...listAccountsApiQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export type RunRegressionAPIError = Fetcher.ErrorWrapper<undefined>;

export type RunRegressionAPIVariables = {
  body?: Schemas.Request;
} & ToolkitContext['fetcherOptions'];

/**
 * Run a unit rate regression for testing and debugging purposes
 */
export const fetchRunRegressionAPI = (variables: RunRegressionAPIVariables, signal?: AbortSignal) =>
  toolkitFetch<Schemas.Response, RunRegressionAPIError, Schemas.Request, {}, {}, {}>({
    url: '/api/v2/admin/regression',
    method: 'post',
    ...variables,
    signal,
  });

/**
 * Run a unit rate regression for testing and debugging purposes
 */
export const useRunRegressionAPI = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Response,
      RunRegressionAPIError,
      RunRegressionAPIVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useToolkitContext();
  return reactQuery.useMutation<Schemas.Response, RunRegressionAPIError, RunRegressionAPIVariables>(
    {
      mutationFn: (variables: RunRegressionAPIVariables) =>
        fetchRunRegressionAPI(deepMerge(fetcherOptions, variables)),
      ...options,
    },
  );
};

export type QueryOperation =
  | {
      path: '/api/v1/blobs/{blobID}';
      operationId: 'getBlob';
      variables: GetBlobVariables;
    }
  | {
      path: '/api/v1/blobs/{blobID}/metadata';
      operationId: 'getBlobMetadata';
      variables: GetBlobMetadataVariables;
    }
  | {
      path: '/api/v1/account';
      operationId: 'getAccount';
      variables: GetAccountVariables;
    }
  | {
      path: '/api/v2/orders';
      operationId: 'getTkv2OrdersAPI';
      variables: GetTkv2OrdersAPIVariables;
    }
  | {
      path: '/api/v2/orders/{orderID}';
      operationId: 'getTkv2OrderAPI';
      variables: GetTkv2OrderAPIVariables;
    }
  | {
      path: '/api/v2/orders/{orderID}/attachments/compSequence';
      operationId: 'getCompSequenceAPI';
      variables: GetCompSequenceAPIVariables;
    }
  | {
      path: '/api/v2/orders/{orderID}/attachments/rentalCompSequence';
      operationId: 'getRentalCompSequenceAPI';
      variables: GetRentalCompSequenceAPIVariables;
    }
  | {
      path: '/api/v2/orders/{orderID}/attachments/additionalFeatures';
      operationId: 'getTkv2AdditionalFeaturesAPI';
      variables: GetTkv2AdditionalFeaturesAPIVariables;
    }
  | {
      path: '/api/v2/orders/{orderID}/attachments/orderFeatures';
      operationId: 'getTkv2OrderFeaturesAPI';
      variables: GetTkv2OrderFeaturesAPIVariables;
    }
  | {
      path: '/api/v2/orders/{orderID}/attachments/alerts';
      operationId: 'getTkv2AlertsAPI';
      variables: GetTkv2AlertsAPIVariables;
    }
  | {
      path: '/api/v2/orders/{orderID}/attachments/priorSales';
      operationId: 'getTkv2PriorSalesAPI';
      variables: GetTkv2PriorSalesAPIVariables;
    }
  | {
      path: '/api/v2/orders/{orderID}/attachments/analytics';
      operationId: 'getTkv2AnalyticsAttachmentAPI';
      variables: GetTkv2AnalyticsAttachmentAPIVariables;
    }
  | {
      path: '/api/v2/orders/{orderID}/attachments/commentary';
      operationId: 'getTkv2CommentaryAttachmentAPI';
      variables: GetTkv2CommentaryAttachmentAPIVariables;
    }
  | {
      path: '/api/v2/orders/{orderID}/attachments/reconciliation';
      operationId: 'getTkv2ReconciliationAttachmentAPI';
      variables: GetTkv2ReconciliationAttachmentAPIVariables;
    }
  | {
      path: '/api/v2/orders/{orderID}/attachments/unitRateModels';
      operationId: 'getTkv2UnitRateModelsAPI';
      variables: GetTkv2UnitRateModelsAPIVariables;
    }
  | {
      path: '/api/v2/orders/{orderID}/attachments/vacantLandSales';
      operationId: 'getVacantLandSales';
      variables: GetVacantLandSalesVariables;
    }
  | {
      path: '/api/v2/orders/{orderID}/attachments/subject';
      operationId: 'getTkv2OrderSubjectAPI';
      variables: GetTkv2OrderSubjectAPIVariables;
    }
  | {
      path: '/api/v2/orders/{orderID}/qaAttempts';
      operationId: 'getQaAttempt';
      variables: GetQaAttemptVariables;
    }
  | {
      path: '/api/v2/orders/{orderID}/attachments/subjectdata';
      operationId: 'getSubjectData';
      variables: GetSubjectDataVariables;
    }
  | {
      path: '/api/v2/orders/{orderID}/attachments/comps';
      operationId: 'getTkv2CompsAPI';
      variables: GetTkv2CompsAPIVariables;
    }
  | {
      path: '/api/v2/orders/{orderID}/attachments/compdata';
      operationId: 'getAllCompData';
      variables: GetAllCompDataVariables;
    }
  | {
      path: '/api/v2/orders/{orderID}/attachments/report';
      operationId: 'getReportAPI';
      variables: GetReportAPIVariables;
    }
  | {
      path: '/api/v2/orders/{orderID}/attachments/marketAdjustments';
      operationId: 'getMarketAdjustments';
      variables: GetMarketAdjustmentsVariables;
    }
  | {
      path: '/api/v2/orders/{orderID}/attachments/marketConditions';
      operationId: 'getTkv2MarketConditionsAPI';
      variables: GetTkv2MarketConditionsAPIVariables;
    }
  | {
      path: '/api/v2/orders/{orderID}/publicRecord';
      operationId: 'getTkv2PublicRecordAPI';
      variables: GetTkv2PublicRecordAPIVariables;
    }
  | {
      path: '/api/v2/orders/{orderID}/publicRecords';
      operationId: 'getTkv2PublicRecordsAPI';
      variables: GetTkv2PublicRecordsAPIVariables;
    }
  | {
      path: '/api/v2/mls';
      operationId: 'getMLSDataAPI';
      variables: GetMLSDataAPIVariables;
    }
  | {
      path: '/api/v2/orders/{orderID}/pairedCostSettings';
      operationId: 'getTkv2OrderPairedCostSettingsAPI';
      variables: GetTkv2OrderPairedCostSettingsAPIVariables;
    }
  | {
      path: '/api/v2/orders/{orderID}/depreciationSettings';
      operationId: 'getTkv2OrderDepreciationSettingsAPI';
      variables: GetTkv2OrderDepreciationSettingsAPIVariables;
    }
  | {
      path: '/api/v2/orders/{orderID}/depreciationSettingsV2';
      operationId: 'getOrderDepreciationSettingsV2API';
      variables: GetOrderDepreciationSettingsV2APIVariables;
    }
  | {
      path: '/api/v2/pairedSales';
      operationId: 'listPairedSalesStudies';
      variables: ListPairedSalesStudiesVariables;
    }
  | {
      path: '/api/v2/pairedSales/{studyID}';
      operationId: 'getPairedSalesStudy';
      variables: GetPairedSalesStudyVariables;
    }
  | {
      path: '/api/v2/orders/{orderID}/property/{propertyID}/imageAnalysis';
      operationId: 'getPropertyImageAnalysis';
      variables: GetPropertyImageAnalysisVariables;
    }
  | {
      path: '/api/v2/compImages/{id}';
      operationId: 'getCompImages';
      variables: GetCompImagesVariables;
    }
  | {
      path: '/api/v2/orders/{orderID}/compRecommendation';
      operationId: 'getCompRecommendation';
      variables: GetCompRecommendationVariables;
    }
  | {
      path: '/api/v2/orders/{orderID}/rentalCompRecommendation';
      operationId: 'getRentalCompRecommendation';
      variables: GetRentalCompRecommendationVariables;
    }
  | {
      path: '/api/v2/stripe/subscriptionStatus';
      operationId: 'getStripeSubscriptionStatus';
      variables: GetStripeSubscriptionStatusVariables;
    }
  | {
      path: '/api/v2/autofill/orchestrate/{orderID}/{propertyId}';
      operationId: 'orchestrateAutofillPropertyAPI';
      variables: OrchestrateAutofillPropertyAPIVariables;
    }
  | {
      path: '/api/v2/orders/{orderID}/marketConditions/marketAggregateData';
      operationId: 'getMarketAggregateDataAPI';
      variables: GetMarketAggregateDataAPIVariables;
    }
  | {
      path: '/api/v2/geocoding/coordinates';
      operationId: 'getCoordinatesForAddressesAPI';
      variables: GetCoordinatesForAddressesAPIVariables;
    }
  | {
      path: '/api/v2/admin/accounts';
      operationId: 'listAccountsAPI';
      variables: ListAccountsAPIVariables;
    }
  | {
      path: '/api/v2/orders/{orderID}/escalate';
      operationId: 'escalateTkv2OrderAPI';
      variables: EscalateTkv2OrderAPIVariables;
    };
